/**
 * The ReactFlow-based DAG editor for a single workflow.
 *
 * Loads:
 *  - the workflow detail (header + active version)
 *  - the closed node catalog (for the palette + the config form schema)
 *
 * State management:
 *  - `nodes` / `edges` are ReactFlow's local state; we sync them into
 *    `tray-shape` (the `WorkflowNode[]` / `WorkflowEdge[]` shape the
 *    backend expects) only at save time.
 *  - Each ReactFlow node's `data` carries the Tray-side shape (kind,
 *    config, label) so the custom node renderer can read it directly.
 *  - Selection is tracked separately; the right panel reflects it.
 *
 * Save flow:
 *  - "Save draft"   → POST /versions, activate=false
 *  - "Save & activate" → POST /versions, activate=true
 *  - A new server-side version is created on every save (immutable
 *    history). The local React state then refreshes from the new
 *    active version on success.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Copy,
  GitCompare,
  LayoutGrid,
  Loader2,
  MoreVertical,
  Pencil,
  Play,
  Plus as PlusIcon,
  Power,
  Radio,
  Redo2,
  RotateCcw,
  Save,
  Square,
  Trash2,
  Undo2,
  X,
} from "lucide-react";
import * as React from "react";
import ReactFlow, {
  addEdge,
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Connection,
  type Edge,
  type Node,
  type NodeChange,
  type NodeProps,
  type NodeTypes,
  type OnConnect,
} from "reactflow";
import "reactflow/dist/style.css";
import { Link, useNavigate, useParams } from "react-router-dom";

import { cn } from "@/lib/cn";

import { ExecutionsListSidebar } from "@/components/workflows/ExecutionsListSidebar";
import {
  PalettePopover,
  TrayActionsEdge,
  type TrayEdgeActionsData,
} from "@/components/workflows/EdgeActionsPlus";
import { PaletteDrawer } from "@/components/workflows/PaletteDrawer";
import { NodeEditorDrawer } from "@/components/workflows/NodeEditorDrawer";
import { definitionKindFields } from "@/components/workflows/definitionKind";
import { NodeIconBadge } from "@/components/workflows/nodeIcons";
import { buildCatalogIndex } from "@/components/workflows/NodePalette";
import { RunDetailsPanel } from "@/components/workflows/RunDetailsPanel";
import { SubWorkflowRunDialog } from "@/components/workflows/SubWorkflowRunDialog";
import {
  SubflowRegionNode,
  type SubflowRegionNodeData,
} from "@/components/workflows/SubflowRegionNode";
import {
  buildSubflowCanvasSlice,
  computeDirtySubflowIds,
  extractSinkBranchLinks,
  extractSubflowSliceFromCanvas,
  isSubflowChildNode,
  isSubflowLinkEdge,
  shouldSaveMain,
  splitMainFromSubflowCanvas,
  subflowChildEdgeId,
  subflowChildNodeId,
  subflowGroupNodeId,
  workflowIdFromGroupId,
} from "@/components/workflows/subflowOrchestration";
import { useLiveEvents } from "@/lib/eventStream";
import { useOdinoBuild } from "@/lib/odinoBuild";
import { type CountableEvent, foldLiveCounts, newLiveCountState } from "@/lib/workflows/liveCounts";
import { autoLayoutPositions } from "@/lib/workflows/autoLayout";
import { SendTestPayloadDialog } from "@/components/workflows/SendTestPayloadDialog";
import { TestRunDrawer } from "@/components/workflows/TestRunDrawer";
import { VersionDiffDialog } from "@/components/workflows/VersionDiffDialog";
import { WorkflowEditMetadataDialog } from "@/components/workflows/WorkflowEditMetadataDialog";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import {
  workflowExecutionsApi,
  workflowsApi,
  type CatalogNode,
  type Direction,
  type ExecutionDetail,
  type NodeKind,
  type TestRunNodeTrace,
  type TestRunResponse,
  type WorkflowDefinition,
  type WorkflowEdge as TrayEdge,
  type WorkflowNode as TrayNode,
} from "@/lib/api-workflows";
import { usePermissions } from "@/lib/permissions";

// ---------- node-data shape carried in ReactFlow nodes --------------------

interface NodeData {
  kind: NodeKind;
  label: string | null;
  config: Record<string, unknown>;
  /** n8n-style node Settings (ADR-0009 Track 2a–2d). */
  execute_once?: boolean;
  notes?: string | null;
  always_output_data?: boolean;
  retry_on_fail?: boolean;
  max_tries?: number;
  retry_wait_ms?: number;
  on_error?: "stop" | "continue" | "continue_error_output";
  /** Per-node execution mode (null = the catalog default). Must round-trip
   *  through trayNodeToFlow/flowToTray/patchNode/drawerTrayNode or every
   *  editor Save silently resets it (the node-level-fields rule). */
  execution_mode?: "per_item" | "once_on_batch" | null;
  spec: CatalogNode | undefined;
  /** Populated by EditorInner after a test-run for visual overlay. */
  traceStatus?: string;
  /** Draft-first reveal (Odino): stagger delay ms for the one-shot entry animation. */
  revealDelay?: number;
  /** Live batch progress (ADR-0067 S2) — {done,total} for a code/loop node
   *  processing a large batch, shown as "item X / N" while it runs. */
  progress?: { done: number; total: number };
  /** True when this node is a leaf — non-sink + has no outgoing edge.
   *  Populated by ``nodesWithLeaf`` memo. Drives the "+" button on the
   *  right side that lets the user extend the flow (N8N parity). */
  isLeaf?: boolean;
  /** Fired when the user clicks the leaf "+" button. ``screenPos`` is
   *  the mouse position so the parent can anchor the PalettePopover
   *  next to the cursor. */
  onAddAfter?: (nodeId: string, screenPos: { x: number; y: number }) => void;
  /** ADR-0008 PR3b3 — for sink nodes, maps branch_id → linked sub-flow
   *  workflow id (null when no sub-flow yet). Drives the per-branch
   *  ``+`` / ``↗`` affordance in the handle row. */
  branchSubflowLinks?: Record<string, string | null>;
  /** Click on `+` next to an unlinked branch handle on a sink.
   *  Triggers sub-flow creation + auto-save of the parent. */
  onCreateBranchSubflow?: (branchId: string) => void;
  /** Click on `↗` next to a linked branch handle — open the sub-flow
   *  editor in a new tab. */
  onOpenBranchSubflow?: (linkedWorkflowId: string) => void;
  /** Set while a create-sub-flow mutation is in flight for this node,
   *  scoped to a specific branch id. Lets the row show a spinner +
   *  disable double-clicks. */
  creatingSubflowBranchId?: string | null;
  /** call_workflow only — open the read-only sub-workflow drill-in (live
   *  during a run). ``childWorkflowId`` comes from the node's config. */
  onWatchSubflow?: (childWorkflowId: string) => void;
  /** N8N-style hover toolbar + context-menu actions on the node. Only
   *  populated in edit mode (absent in past-run / read-only, so the
   *  affordance simply doesn't render). ``onOpenMenu`` receives the
   *  cursor position so the menu anchors next to the click. */
  onRunNode?: (id: string) => void;
  onDuplicateNode?: (id: string) => void;
  onDeleteNode?: (id: string) => void;
  onOpenNode?: (id: string) => void;
  onOpenMenu?: (id: string, pos: { x: number; y: number }) => void;
}

// ---------- custom node renderer -----------------------------------------

// Every node renders on the SAME neutral background regardless of
// category — uniform colour means an "active" or "failed" status ring
// jumps out instantly during a run. We use the design-system tokens
// (``elevated`` / ``foreground`` / ``border``) so the chip looks right
// in both light and dark mode without per-theme branching.
const NODE_BG = "bg-elevated text-foreground border-border";

// outputHandlesFor moved to ./outputHandles.ts so tests can import the
// pure mapper without dragging in ReactFlow, EventStream, Monaco, etc.
// Re-exported here so external imports of the symbol keep working.
import { outputHandlesFor, type OutputHandleSpec } from "./outputHandles";
export { outputHandlesFor, type OutputHandleSpec };

// ---------- graph validation ---------------------------------------------

// validateGraph + GraphIssue moved to ./workflowGraphValidation.ts so
// the pure logic is unit-testable without dragging in ReactFlow /
// EventStream / Monaco (same pattern as outputHandlesFor). Re-exported
// here so external imports of the symbols keep working.
import { validateGraph, type GraphIssue } from "./workflowGraphValidation";
export { validateGraph, type GraphIssue };
import { isEntrypointKind, wouldCreateCycle } from "./nodeGraph";

function TrayNodeBox({ id, data, selected }: NodeProps<NodeData>) {
  const isSource = data.spec?.category === "source";
  const isSink = data.spec?.category === "sink";
  // Entry points (triggers + read_canonical) have no input — nothing can
  // connect INTO them. read_canonical is IO_CANONICAL (not "source") yet
  // IS an entry point, so gate the target handle on this, not on isSource.
  const isEntrypoint = isEntrypointKind(data.kind, data.spec?.category);

  // After a test-run / past-run overlay, surface the per-node status
  // as a saturated ring around the otherwise-uniform dark box.
  // ``running`` is the live-debug case (executor signaled the node is
  // currently in flight) — we paint a glowing emerald ring to make the
  // "where are we now" obvious.
  // Failed nodes get the same "loud" treatment as running (thick ring
  // + colored glow) but in true red — so a failed run jumps out from
  // a long flow with everything else green, instead of having to spot
  // a single 2px border. Uses the ``danger`` token (the theme has
  // ``danger`` defined, NOT ``destructive`` — using the latter
  // silently fell back to a violet-ish browser default).
  // Cancelled / stopped nodes are dimmed grey: the run was aborted
  // mid-flight, so the node isn't a failure (no error) but it never
  // produced output either.
  // Traffic-light by HUE so the three states are unmistakable at a glance:
  //   running   = AMBER/ORANGE (in-flight) — distinct from the green "done"
  //               so a running node doesn't blend with its finished
  //               neighbours; thick ring + an INDETERMINATE progress sweep
  //               (``tray-running-sweep``, see index.css) rather than a flat
  //               pulse, so "work in progress" reads at a glance.
  //   succeeded = GREEN, solid ring (calm, no glow).
  //   failed    = RED, loud ring + glow + tint.
  const statusRing =
    data.traceStatus === "failed"
      ? " ring-2 ring-danger shadow-[0_0_18px_rgba(239,68,68,0.65)] bg-danger/10"
      : data.traceStatus === "running"
        ? " tray-node-running bg-amber-400/5"
        : data.traceStatus === "cancelled" || data.traceStatus === "stopped"
          ? " ring-1 ring-zinc-500/70 opacity-60 grayscale"
          : data.traceStatus === "skipped"
            ? " opacity-50"
            : data.traceStatus === "succeeded"
              ? " ring-2 ring-emerald-500 bg-emerald-500/5"
              : "";

  const namedHandles = outputHandlesFor(data.kind, data.config);
  // ADR-0008 PR3 — sinks now ALSO expose handles (succeeded /
  // dead_letter implicit + any user-declared branch). Used by the
  // reactive sub-flow trigger. Before PR3 sinks had no outputs and
  // the renderer below skipped them with `!isSink`; we now want the
  // handles visible on the canvas so the operator can hook sub-flows.
  const hasNamedHandles = namedHandles.length > 0;
  // ADR-0009 Track 2e — "Continue (using error output)" adds a dedicated
  // `error` source handle to ANY non-source/non-sink node (a node-level
  // setting, orthogonal to per-kind handles). The executor routes the
  // failure down edges with source_handle="error".
  const errorOutput = !isSource && !isSink && data.on_error === "continue_error_output";
  // Rows to render on the right. With named handles we append the error
  // row; for a plain single-output node we materialise BOTH the default
  // output (id undefined → source_handle null) and the error row so the
  // two handles don't overlap.
  const renderHandles: { id?: string; label: string; isError?: boolean }[] = hasNamedHandles
    ? [
        ...namedHandles.map((h) => ({ id: h.id, label: h.label })),
        ...(errorOutput ? [{ id: "error", label: "Error", isError: true }] : []),
      ]
    : errorOutput
      ? [{ label: "Out" }, { id: "error", label: "Error", isError: true }]
      : [];

  return (
    <div
      className={
        "group relative min-w-[180px] border px-3 py-2 text-xs shadow-sm transition " +
        NODE_BG +
        (selected ? " ring-2 ring-accent" : "") +
        statusRing +
        (data.revealDelay != null ? " tray-node-reveal" : "")
      }
      style={data.revealDelay != null ? { animationDelay: `${data.revealDelay}ms` } : undefined}
      // Handles are now rendered in natural flow (each row is 22px) so
      // the box grows automatically — no explicit minHeight needed.
    >
      {/* N8N-style hover toolbar (Run / Duplicate / Delete / Menu). Floats
          above the node, revealed on hover (or keyboard focus-within).
          ``nodrag`` keeps a click from starting a node drag; every button
          stops propagation so it doesn't select / open the node. Absent
          callbacks (past-run / read-only) hide the whole strip. */}
      {(data.onRunNode || data.onDuplicateNode || data.onDeleteNode || data.onOpenMenu) && (
        <div className="nodrag absolute -top-8 right-0 z-20 flex items-center gap-0.5 rounded-md border border-border bg-surface p-0.5 opacity-0 shadow-md transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          {data.onRunNode && (
            <button
              type="button"
              title="Esegui nodo"
              className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              onClick={(e) => {
                e.stopPropagation();
                data.onRunNode?.(id);
              }}
            >
              <Play className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
          )}
          {data.onDuplicateNode && (
            <button
              type="button"
              title="Duplica nodo"
              className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              onClick={(e) => {
                e.stopPropagation();
                data.onDuplicateNode?.(id);
              }}
            >
              <Copy className="h-3.5 w-3.5" strokeWidth={1.75} />
            </button>
          )}
          {data.onDeleteNode && (
            <button
              type="button"
              title="Elimina nodo"
              className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-danger/10 hover:text-danger"
              onClick={(e) => {
                e.stopPropagation();
                const name = data.label || data.spec?.label || data.kind;
                if (window.confirm(`Eliminare il nodo "${name}"?`)) {
                  data.onDeleteNode?.(id);
                }
              }}
            >
              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
            </button>
          )}
          {data.onOpenMenu && (
            <button
              type="button"
              title="Altre azioni"
              className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              onClick={(e) => {
                e.stopPropagation();
                data.onOpenMenu?.(id, { x: e.clientX, y: e.clientY });
              }}
            >
              <MoreVertical className="h-3.5 w-3.5" strokeWidth={1.75} />
            </button>
          )}
        </div>
      )}
      {/* Target (input) handle. Sources don't have inputs … EXCEPT
          ``delivery_trigger`` which IS a source by category (it starts
          a sub-flow), but the sub-flow is wired in via a virtual edge
          from the parent sink's branch handle landing on this trigger.
          Without a target handle React Flow has nothing to attach the
          edge to — and the connection silently fails to render
          (ADR-0008 PR3b3c). */}
      {(!isEntrypoint || data.kind === "delivery_trigger") && (
        <Handle
          type="target"
          position={Position.Left}
          className="!h-2 !w-2 !bg-foreground max-sm:!h-5 max-sm:!w-5"
        />
      )}
      {/* call_workflow — a corner "watch" button opens the read-only
          sub-workflow drill-in (live during a run: the child emits SSE on
          the parent execution_id). ``config.workflow_id`` is always known,
          so it works even before/while running. */}
      {data.kind === "call_workflow" &&
        typeof data.config?.workflow_id === "string" &&
        data.config.workflow_id && (
          <button
            type="button"
            title="Osserva il sub-workflow (live durante l'esecuzione)"
            onClick={(e) => {
              e.stopPropagation();
              data.onWatchSubflow?.(data.config.workflow_id as string);
            }}
            className="absolute -right-2 -top-2 z-10 inline-flex h-5 w-5 items-center justify-center rounded-full border border-border bg-elevated text-[11px] leading-none text-muted-foreground shadow-sm hover:text-foreground"
          >
            ⤢
          </button>
        )}
      <div className="flex items-center gap-2">
        <NodeIconBadge kind={data.kind} category={data.spec?.category} size={28} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[10px] uppercase tracking-wider text-muted-foreground">
            {data.spec?.label ?? data.kind}
          </div>
          <div className="truncate font-medium text-foreground">
            {data.label || data.spec?.label || data.kind}
          </div>
          {/* webhook_trigger response_mode chip — only when non-default,
              so the canvas at-a-glance shows whether this endpoint
              answers synchronously (last_node / respond_node / streaming)
              vs the fire-and-forget default. */}
          {data.kind === "webhook_trigger" &&
            typeof data.config?.response_mode === "string" &&
            data.config.response_mode !== "immediately" && (
              <div className="mt-0.5 inline-flex items-center rounded border border-sky-400/40 bg-sky-400/10 px-1 py-px text-[9px] font-medium uppercase tracking-wider text-sky-300">
                → {data.config.response_mode.replace("_", " ")}
              </div>
            )}
        </div>
      </div>

      {/* ADR-0067 S2 — live batch progress "item X / N" while a code/loop
          node grinds through a large fan-out, with a thin fill bar so a
          long run doesn't look frozen. */}
      {data.progress && data.progress.total > 0 && (
        <div className="mt-1.5">
          <div className="flex items-center justify-between text-[9px] tabular-nums text-emerald-300">
            <span>
              {data.progress.done.toLocaleString()} / {data.progress.total.toLocaleString()}
            </span>
            <span>{Math.floor((data.progress.done / data.progress.total) * 100)}%</span>
          </div>
          <div className="mt-0.5 h-1 overflow-hidden rounded bg-emerald-900/40">
            <div
              className="h-full bg-emerald-400 transition-[width]"
              style={{
                width: `${Math.min(100, (data.progress.done / data.progress.total) * 100)}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Named handles — vertically stacked on the right with their
          labels. Each handle has a unique `id` matching the WorkflowEdge
          source_handle the user picks when drawing an edge.
          Each row is `position: relative` so the Handle (absolutely
          positioned by ReactFlow with `right:0; top:50%`) self-centers
          inside its own row. Earlier versions used a manual top offset
          which broke when the header height changed and produced
          orphan-looking dots below the node. */}
      {renderHandles.length > 0 && (
        <div className="mt-2 flex flex-col">
          {renderHandles.map((h) => {
            // ADR-0008 PR3b3 — on sinks only, show a per-branch
            // sub-flow affordance. Linked state comes from
            // data.branchSubflowLinks. (Error / default rows are never
            // on sinks, so this stays sink-scoped.)
            const linkedId = isSink && h.id ? (data.branchSubflowLinks?.[h.id] ?? null) : null;
            const isCreating = isSink && !!h.id && data.creatingSubflowBranchId === h.id;
            return (
              <div
                key={h.id ?? "out"}
                className={
                  "relative flex h-[22px] items-center justify-end gap-1 pr-3 text-[10px] " +
                  (h.isError ? "text-danger" : "text-muted-foreground")
                }
              >
                {isSink && data.onCreateBranchSubflow && !linkedId && h.id && (
                  <button
                    type="button"
                    title={`Create sub-flow for "${h.label || h.id}"`}
                    onClick={(e) => {
                      e.stopPropagation();
                      data.onCreateBranchSubflow?.(h.id!);
                    }}
                    disabled={isCreating}
                    className={
                      "inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-border bg-background text-[9px] leading-none transition hover:border-foreground hover:text-foreground disabled:opacity-40 " +
                      (isCreating ? "animate-pulse" : "")
                    }
                  >
                    +
                  </button>
                )}
                {isSink && linkedId && (
                  <span
                    className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-emerald-400/40 bg-emerald-400/10 text-[8px] text-emerald-300"
                    title="Linked to an inline sub-flow region on the canvas"
                  >
                    ✓
                  </span>
                )}
                <span className="font-mono">{h.label || h.id}</span>
                <Handle
                  type="source"
                  position={Position.Right}
                  id={h.id}
                  className={
                    "!h-2 !w-2 max-sm:!h-5 max-sm:!w-5 " +
                    (h.isError ? "!bg-danger" : "!bg-foreground")
                  }
                  style={{ right: -4 }}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Single-output default — when the node has no rendered handle
          rows AND isn't a sink (sinks always have at least the implicit
          succeeded / dead_letter handles via outputHandlesFor). */}
      {renderHandles.length === 0 && !isSink && (
        <Handle
          type="source"
          position={Position.Right}
          className="!h-2 !w-2 !bg-foreground max-sm:!h-5 max-sm:!w-5"
        />
      )}

      {/* N8N-style "extend the flow" affordance: when this node is a
          leaf (non-sink with no outgoing edges), float a "+" pill
          off its right edge. Click → open the palette popover →
          create a new node + edge. Visible always (not just on
          hover) since these are dead-ends the user almost certainly
          wants to extend. */}
      {data.isLeaf && !isSink && data.onAddAfter && (
        <button
          type="button"
          title="Add next node"
          onClick={(e) => {
            e.stopPropagation();
            data.onAddAfter?.(
              // NodeProps doesn't pass `id` directly via destructure —
              // we read it from the rendered DOM ancestor via the
              // closest ``.react-flow__node`` element's data-id attr,
              // which ReactFlow stamps.
              (e.currentTarget.closest(".react-flow__node") as HTMLElement | null)?.getAttribute(
                "data-id",
              ) ?? "",
              { x: e.clientX, y: e.clientY },
            );
          }}
          className={cn(
            "absolute top-1/2 -translate-y-1/2",
            // Sit ~24px to the right of the node, beyond the output
            // handle dot so it doesn't fight for clicks with the
            // ReactFlow connect-from-handle gesture.
            "left-full ml-6",
            "flex h-6 w-6 items-center justify-center rounded-full",
            "border border-border bg-surface shadow-md",
            "text-foreground hover:bg-accent hover:text-accent-foreground",
            "transition-transform hover:scale-110",
          )}
        >
          <PlusIcon className="h-3.5 w-3.5" strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}

/** N8N-style node context menu — opened by the node's ``⋮`` toolbar button
 *  OR by a right-click on the node. Rendered at the page root as a fixed
 *  overlay anchored at the cursor. Closes on outside-click (capture phase,
 *  so ReactFlow's bubble-phase ``stopPropagation`` can't swallow it) + Esc.
 *  Only the actions we actually possess are offered. */
function NodeContextMenu({
  x,
  y,
  canWrite,
  onClose,
  onOpen,
  onRun,
  onDuplicate,
  onDelete,
}: {
  x: number;
  y: number;
  canWrite: boolean;
  onClose: () => void;
  onOpen: () => void;
  onRun: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as globalThis.Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("mousedown", onDown, true);
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("mousedown", onDown, true);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [onClose]);

  const left = Math.min(x, window.innerWidth - 208);
  const top = Math.min(y, window.innerHeight - 200);

  const rowCls =
    "flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-accent hover:text-accent-foreground disabled:opacity-40 disabled:hover:bg-transparent";

  return (
    <div
      ref={ref}
      className="fixed z-50 w-52 overflow-hidden rounded-md border border-border bg-surface py-1 text-xs shadow-lg"
      style={{ left, top }}
    >
      <button type="button" className={rowCls} onClick={onOpen}>
        <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
        Apri
      </button>
      <button type="button" className={rowCls} onClick={onRun} disabled={!canWrite}>
        <Play className="h-3.5 w-3.5" strokeWidth={2} />
        Esegui nodo
      </button>
      <button type="button" className={rowCls} onClick={onDuplicate} disabled={!canWrite}>
        <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
        Duplica
        <span className="ml-auto text-[10px] text-muted-foreground">⌘D</span>
      </button>
      <div className="my-1 border-t border-border/60" />
      <button
        type="button"
        className={
          "flex w-full items-center gap-2 px-3 py-1.5 text-left text-danger hover:bg-danger/10 disabled:opacity-40 disabled:hover:bg-transparent"
        }
        onClick={onDelete}
        disabled={!canWrite}
      >
        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
        Elimina
        <span className="ml-auto text-[10px] opacity-70">Del</span>
      </button>
    </div>
  );
}

const NODE_TYPES: NodeTypes = {
  tray: TrayNodeBox,
  // ADR-0008 PR3b3b — bordered region wrapping an inline sub-flow.
  // Children of this node (set via parentNode + extent="parent") are
  // the sub-flow's own delivery_trigger source + downstream nodes.
  subflowRegion: SubflowRegionNode,
};

// Single custom edge kind, with hover-revealed "+" / trash actions.
// All edges in the editor render through this so the affordance is
// uniform; the per-edge callbacks (insert / delete) ride on
// ``data.onInsertClick`` / ``data.onDelete``, populated by the
// editor when it shapes the edge list before passing to ReactFlow.
const EDGE_TYPES = { trayActions: TrayActionsEdge };

// ---------- helpers: tray <-> reactflow ----------------------------------

function trayNodeToFlow(n: TrayNode, catalogIdx: Record<NodeKind, CatalogNode>): Node<NodeData> {
  return {
    id: n.id,
    type: "tray",
    position: { x: n.position?.x ?? 0, y: n.position?.y ?? 0 },
    data: {
      kind: n.kind,
      label: n.label ?? null,
      config: n.config ?? {},
      execute_once: n.execute_once ?? false,
      notes: n.notes ?? null,
      always_output_data: n.always_output_data ?? false,
      retry_on_fail: n.retry_on_fail ?? false,
      max_tries: n.max_tries ?? 3,
      retry_wait_ms: n.retry_wait_ms ?? 1000,
      on_error: n.on_error ?? "stop",
      execution_mode: n.execution_mode ?? null,
      spec: catalogIdx[n.kind],
    },
  };
}

/**
 * Workflows saved via API (curl / scripts / templates seeded before the
 * UI existed) commonly have all nodes at (0,0) — they end up stacked
 * on top of each other in the canvas. We detect that and lay them out
 * left-to-right in topological order so the editor opens to a usable
 * graph.
 *
 * The layout is only applied if it's clearly needed (all positions
 * collapsed to a single point). User-positioned graphs are left alone.
 */
function autoLayoutIfNeeded(trayNodes: TrayNode[], edges: TrayEdge[]): TrayNode[] {
  if (trayNodes.length <= 1) return trayNodes;

  const distinctPositions = new Set(
    trayNodes.map((n) => `${n.position?.x ?? 0},${n.position?.y ?? 0}`),
  );
  if (distinctPositions.size > 1) return trayNodes;

  // All nodes share the same position — auto-layout. Topological
  // depth = longest path from any source, gives a clean column index.
  const indeg = new Map<string, number>(trayNodes.map((n) => [n.id, 0]));
  const adj = new Map<string, string[]>(trayNodes.map((n) => [n.id, []]));
  for (const e of edges) {
    indeg.set(e.target, (indeg.get(e.target) ?? 0) + 1);
    adj.get(e.source)?.push(e.target);
  }
  const depth = new Map<string, number>();
  const queue: string[] = [];
  for (const [id, d] of indeg) {
    if (d === 0) {
      depth.set(id, 0);
      queue.push(id);
    }
  }
  while (queue.length) {
    const nid = queue.shift()!;
    const d = depth.get(nid) ?? 0;
    for (const nxt of adj.get(nid) ?? []) {
      depth.set(nxt, Math.max(depth.get(nxt) ?? 0, d + 1));
      const indegLeft = (indeg.get(nxt) ?? 1) - 1;
      indeg.set(nxt, indegLeft);
      if (indegLeft === 0) queue.push(nxt);
    }
  }

  // Group by depth → y coordinate stacks nodes at same depth vertically.
  const perDepth = new Map<number, number>();
  return trayNodes.map((n) => {
    const d = depth.get(n.id) ?? 0;
    const yIdx = perDepth.get(d) ?? 0;
    perDepth.set(d, yIdx + 1);
    return {
      ...n,
      position: { x: 80 + d * 240, y: 80 + yIdx * 120 },
    };
  });
}

function trayEdgeToFlow(e: TrayEdge): Edge {
  return {
    id: e.id,
    source: e.source,
    target: e.target,
    // ReactFlow uses ``sourceHandle`` (camelCase) for the same concept
    // we call ``source_handle`` on the wire — translate at the boundary.
    sourceHandle: e.source_handle ?? undefined,
    // Use the custom edge type for the N8N-style hover actions.
    // The data.onInsertClick / data.onDelete callbacks are populated
    // by the editor at render time (see the useMemo below the
    // ReactFlow component) — they need closures over the editor's
    // setNodes/setEdges, which aren't available here at module scope.
    type: "trayActions",
  };
}

function flowToTray(
  flowNodes: Node<NodeData>[],
  flowEdges: Edge[],
): { nodes: TrayNode[]; edges: TrayEdge[] } {
  return {
    nodes: flowNodes.map((fn) => ({
      id: fn.id,
      kind: fn.data.kind,
      label: fn.data.label,
      config: fn.data.config,
      execute_once: fn.data.execute_once ?? false,
      notes: fn.data.notes ?? null,
      always_output_data: fn.data.always_output_data ?? false,
      retry_on_fail: fn.data.retry_on_fail ?? false,
      max_tries: fn.data.max_tries ?? 3,
      retry_wait_ms: fn.data.retry_wait_ms ?? 1000,
      on_error: fn.data.on_error ?? "stop",
      execution_mode: fn.data.execution_mode ?? null,
      position: { x: fn.position.x, y: fn.position.y },
    })),
    edges: flowEdges.map((fe) => ({
      id: fe.id,
      source: fe.source,
      target: fe.target,
      source_handle: fe.sourceHandle ?? null,
    })),
  };
}

// ---------- editor body --------------------------------------------------

/** Forward-reachable node ids from ``from`` (BFS over edges; loop back-edges
 *  are tolerated via the visited set). Used to classify the drawer node vs the
 *  pinned node for the step-by-step "Execute Node" button label. */
function forwardReachable(edges: { source: string; target: string }[], from: string): Set<string> {
  const out = new Map<string, string[]>();
  for (const e of edges) {
    const list = out.get(e.source);
    if (list) list.push(e.target);
    else out.set(e.source, [e.target]);
  }
  const seen = new Set<string>();
  const stack = [...(out.get(from) ?? [])];
  while (stack.length) {
    const n = stack.pop()!;
    if (seen.has(n)) continue;
    seen.add(n);
    for (const t of out.get(n) ?? []) if (!seen.has(t)) stack.push(t);
  }
  return seen;
}

function EditorInner({ workflowId }: { workflowId: string }) {
  const qc = useQueryClient();
  // Workflow editing is gated on the real permission, not "is admin":
  // a non-writer opens the editor READ-ONLY (canvas locked, node drawer
  // read-only, no edit toolbar) so there's no "I can change nodes but
  // can't save" confusion.
  const { can } = usePermissions();
  const canWrite = can("workflows", "write");
  const canActivate = can("workflows", "activate");
  const canDelete = can("workflows", "delete");
  const rf = useReactFlow();
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const idCounterRef = React.useRef(0);

  const wfQuery = useQuery({
    queryKey: ["workflow", workflowId],
    queryFn: () => workflowsApi.get(workflowId),
  });
  const catalogQuery = useQuery({
    queryKey: ["workflow-catalog"],
    queryFn: () => workflowsApi.catalog(),
    staleTime: 5 * 60 * 1000,
  });

  const catalogIdx = React.useMemo(
    () =>
      catalogQuery.data
        ? buildCatalogIndex(catalogQuery.data.nodes)
        : ({} as Record<NodeKind, CatalogNode>),
    [catalogQuery.data],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState<NodeData>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  /** Which node, if any, has the NodeEditorDrawer open. Decoupled from
   *  ``selectedId`` so a single click only selects (for hotkeys + visual
   *  ring) — the drawer opens on **double**-click. Avoids the n8n-style
   *  "every click opens the inspector" behaviour the user wants to avoid. */
  const [drawerNodeId, setDrawerNodeId] = React.useState<string | null>(null);
  // N8N-style per-node context menu (⋮ button OR right-click). Anchored
  // at the cursor; rendered at the page root.
  const [nodeMenu, setNodeMenu] = React.useState<{
    id: string;
    x: number;
    y: number;
  } | null>(null);
  // Bumped by the hover-toolbar / menu "Run node" action; the drawer opens
  // for that node and auto-fires its Execute Node once per bump.
  const [autoRunToken, setAutoRunToken] = React.useState(0);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [testRunOpen, setTestRunOpen] = React.useState(false);
  const [webhookOpen, setWebhookOpen] = React.useState(false);
  const [diffOpen, setDiffOpen] = React.useState(false);
  const [editMetadataOpen, setEditMetadataOpen] = React.useState(false);
  // node_id → status from the last test-run, used for canvas highlighting.
  const [traceStatus, setTraceStatus] = React.useState<Record<string, string>>({});
  // node_id → {done,total} live batch progress (ADR-0067 S2), overlaid on a
  // running code/loop node so the canvas shows "item X / N" instead of a
  // frozen spinner. Cleared alongside traceStatus at the start of each run.
  const [nodeProgress, setNodeProgress] = React.useState<
    Record<string, { done: number; total: number }>
  >({});
  // Per-node LIVE output count (node_finished.items_out), so the per-edge item
  // count pill appears the moment a node finishes — not only at the end of the
  // whole run (activeRunDetail). A loop-body node emits once per batch, so we
  // SUM its items_out across batches for the total. Cleared at each run start.
  const [nodeLiveCount, setNodeLiveCount] = React.useState<Record<string, number>>({});
  // The ACCUMULATOR behind `nodeLiveCount`. It must NOT be recomputed from the
  // event buffer: that's a 200-event newest-first ring shared app-wide, so a
  // long run evicts its own early events and a recomputed sum would shrink —
  // the counts appeared, then vanished mid-run. See lib/workflows/liveCounts.
  const liveCountRef = React.useRef(newLiveCountState());
  // Last in-editor test-run result. Drives the bottom RunDetailsPanel
  // when the user is NOT viewing a past run, so launching a Test Run
  // from Editor mode populates the same inspector as Executions mode.
  const [editorTestRun, setEditorTestRun] = React.useState<TestRunResponse | null>(null);
  /** execution_id of an in-flight run we're listening to for live
   *  per-node events. Set when the user fires a test-run (we pick the
   *  execution_id off the EXECUTION_STARTED SSE event); cleared on
   *  EXECUTION_FINISHED. ``null`` means no live subscription. */
  const [liveExecutionId, setLiveExecutionId] = React.useState<string | null>(null);
  /** True while we're WAITING for the EXECUTION_STARTED that follows
   *  a "Test run" click. Bracket so we don't pick up some unrelated
   *  execution that happened to start in another tab. */
  const armedForLiveRef = React.useRef(false);
  // Set when the user hits Stop. A sync test_run can't be cancelled
  // server-side, so its promise still resolves later — this flag makes the
  // late onRunComplete / onTraceUpdate a no-op so the canvas doesn't
  // re-light nodes the user already stopped watching.
  const runCancelledRef = React.useRef(false);
  // When set, the editor is in "view past execution" mode: it overlays
  // node statuses from this specific historical run and disables
  // canvas editing. Picked from the Executions sidebar.
  const [viewedRunId, setViewedRunId] = React.useState<string | null>(null);
  // n8n-style step debug state. ``pendingStopAt`` is the node id
  // armed for the NEXT TestRunDrawer open (set by "Run up to here").
  // ``pinnedExecutionId`` + ``pinnedNodeId`` are the result of a
  // successful step-debug run — the toolbar shows a chip with a
  // Continue + Clear button, and the canvas marks the pinned node.
  const [pendingStopAt, setPendingStopAt] = React.useState<string | null>(null);
  const [pinnedExecutionId, setPinnedExecutionId] = React.useState<string | null>(null);
  const [pinnedNodeId, setPinnedNodeId] = React.useState<string | null>(null);
  // Step-by-step "Execute Node" resume (TE-F): after a node runs we PIN the
  // execution; the NEXT Execute Node on the same (structurally unchanged) graph
  // RESUMES from it — reusing the pinned upstream + the parent's input (no
  // re-prompt), re-running only the clicked node. The signature is STRUCTURE
  // only (node ids + edges), so tweaking a node's CONFIG keeps the pin alive
  // (the fast debug loop); adding/removing a node/edge invalidates it.
  const structuralSignature = React.useMemo(
    () =>
      JSON.stringify({
        n: nodes.map((n) => n.id).sort(),
        e: edges.map((e) => `${e.source}>${e.sourceHandle ?? ""}>${e.target}`).sort(),
      }),
    [nodes, edges],
  );
  const pinnedSignatureRef = React.useRef<string | null>(null);
  // Bump this whenever the pin changes so the relation memo below recomputes
  // (the signature lives on a ref, which isn't a reactive dep).
  const [pinNonce, setPinNonce] = React.useState(0);
  // Classify the drawer node vs the pinned node for the "Execute Node" button
  // (position-aware): SAME node → re-execute; a node DOWNSTREAM of the pin →
  // continue (resume); anything else (upstream / unrelated / no valid pin) →
  // a fresh run. Only same/downstream carry the pin's execution id.
  const resumeInfo = React.useMemo<{
    executionId: string | null;
    kind: "rerun" | "continue" | null;
  }>(() => {
    const valid =
      !!pinnedExecutionId &&
      !!pinnedNodeId &&
      !!drawerNodeId &&
      pinnedSignatureRef.current === structuralSignature;
    if (!valid) return { executionId: null, kind: null };
    if (drawerNodeId === pinnedNodeId) {
      return { executionId: pinnedExecutionId, kind: "rerun" };
    }
    const downstream = forwardReachable(
      edges.map((e) => ({ source: e.source, target: e.target })),
      pinnedNodeId!,
    );
    if (downstream.has(drawerNodeId!)) {
      return { executionId: pinnedExecutionId, kind: "continue" };
    }
    return { executionId: null, kind: null }; // upstream / unrelated → fresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinnedExecutionId, pinnedNodeId, drawerNodeId, structuralSignature, edges, pinNonce]);
  // Bottom logs panel — collapsed by default; auto-opens on first
  // failure to make the error immediately visible. Height is
  // persisted across reloads so a power user who likes a tall panel
  // doesn't have to drag it back up every session.
  const [logsOpen, setLogsOpen] = React.useState(false);
  const [logsHeight, setLogsHeightRaw] = React.useState<number>(() => {
    if (typeof window === "undefined") return 320;
    const stored = window.localStorage.getItem("tray.workflowEditor.logsHeight");
    const n = stored ? Number.parseInt(stored, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : 320;
  });
  const setLogsHeight = React.useCallback((h: number) => {
    setLogsHeightRaw(h);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("tray.workflowEditor.logsHeight", String(Math.round(h)));
    }
  }, []);

  /** Snapshot the current canvas as a backend-shaped WorkflowDefinition.
   *  Shared by both the TestRunDrawer and the NodeEditorDrawer
   *  ("Execute Node") so the same definition is used regardless of
   *  which entry point fires the run. The kind-aware split mirrors
   *  the version-save mutation — orchestration must omit
   *  direction/canonical, canonical must include them.
   *
   *  We read from ``wfQuery.data`` (not ``wf``) because the helper is
   *  declared at the top of the component, BEFORE the ``wf`` binding
   *  that exists only after the loading-guard. The render path always
   *  goes through the guard before this callback runs, so the data
   *  is populated by the time anyone calls it. */
  // ADR-0065 T1-4 — the workflow's optional on-failure handler (a definition
  // field, not a row column), seeded from the loaded definition on hydration
  // and edited via the metadata dialog. It rides every definition build
  // (buildLiveDefinition + the main save) and the dirty snapshot, so it
  // persists through the normal editor Save like any other definition content.
  const [errorWorkflowId, setErrorWorkflowId] = React.useState<string | null>(null);

  const buildLiveDefinition = React.useCallback<() => WorkflowDefinition>(() => {
    // ADR-0008 PR3b3b — strip sub-flow regions + children before
    // serialising. They live in their own WorkflowDefinitions.
    const mainNodes = nodes.filter(
      (n) => n.type !== "subflowRegion" && n.parentNode === undefined && !isSubflowChildNode(n),
    );
    const mainEdges = edges.filter(
      (e) =>
        !isSubflowChildNode({ id: e.source }) &&
        !isSubflowChildNode({ id: e.target }) &&
        !isSubflowChildNode(e) &&
        !isSubflowLinkEdge(e),
    );
    const { nodes: trayNodes, edges: trayEdges } = flowToTray(mainNodes, mainEdges);
    const wfData = wfQuery.data;
    return {
      name: wfData?.name ?? "",
      ...definitionKindFields(wfData ?? {}),
      description: wfData?.description ?? null,
      error_workflow_id: errorWorkflowId,
      nodes: trayNodes,
      edges: trayEdges,
    };
  }, [nodes, edges, wfQuery.data, errorWorkflowId]);
  /** Top-of-canvas pill toggle. "editor" shows the node palette on the
   *  right; "executions" replaces the palette with a list of past runs
   *  on the LEFT and puts the canvas in read-only overlay mode for the
   *  selected run. Clearing the viewedRunId when leaving Executions
   *  guarantees we don't keep stale overlays around. */
  const [viewMode, setViewMode] = React.useState<"editor" | "executions">("editor");
  React.useEffect(() => {
    if (viewMode === "editor") setViewedRunId(null);
  }, [viewMode]);

  // Which version's definition is currently loaded into the canvas.
  // ``null`` = "follow the active version" (default). When the user
  // browses a past version we set this to its id so re-renders don't
  // snap back to active.
  const [loadedVersionId, setLoadedVersionId] = React.useState<string | null>(null);

  // Snapshot of the loaded definition (JSON-stringified) — used to
  // detect "modified since load" cheaply. Compared against the current
  // nodes/edges on every render of the dirty indicator.
  const [loadedSnapshot, setLoadedSnapshot] = React.useState<string>("");
  // ADR-0008 PR3b3c — one snapshot per linked sub-flow, captured at
  // hydration time. Used by the dirty-detection + N-PATCH save loop
  // to figure out which sub-flow definitions diverged from what the
  // backend has, so we only POST createVersion on the ones that
  // actually changed.
  const [loadedSubflowSnapshots, setLoadedSubflowSnapshots] = React.useState<
    Record<string, string>
  >({});

  // Bumped to force a full re-hydration without any other dep changing —
  // used by "Discard changes". Re-running the hydration effect rebuilds
  // the main flow AND re-splats every sub-flow region (the manual
  // main-only rebuild discard used to do dropped the regions entirely).
  const [hydrationNonce, setHydrationNonce] = React.useState(0);

  // Convenience: which version are we actually viewing?
  const effectiveVersionId = loadedVersionId ?? wfQuery.data?.active_version_id ?? null;
  const isViewingActive =
    effectiveVersionId !== null && effectiveVersionId === wfQuery.data?.active_version_id;
  const viewedVersion = React.useMemo(() => {
    if (!wfQuery.data) return null;
    if (effectiveVersionId === wfQuery.data.active_version_id) {
      return wfQuery.data.active_version
        ? { id: wfQuery.data.active_version.id, version: wfQuery.data.active_version.version }
        : null;
    }
    return (wfQuery.data.recent_versions ?? []).find((v) => v.id === effectiveVersionId) ?? null;
  }, [wfQuery.data, effectiveVersionId]);

  // Hydrate state from the loaded version. When ``loadedVersionId`` is
  // null we follow ``active_version``; otherwise we explicitly fetch
  // the chosen version's definition and load it.
  const explicitVersionQ = useQuery({
    queryKey: ["workflow-version", workflowId, loadedVersionId],
    queryFn: () => workflowsApi.getVersion(workflowId, loadedVersionId!),
    enabled: loadedVersionId !== null && loadedVersionId !== wfQuery.data?.active_version_id,
  });

  // Draft-first (Odino): land the operator on the newest DRAFT when (a) the
  // workflow has NO active version (a fresh Odino create — the canvas would
  // otherwise be empty) or (b) Odino just committed a draft for THIS workflow
  // (the reveal signal). The toolbar then shows "Activate vN" — the operator's
  // click IS the activation (Odino never activates).
  //
  // The reveal is consumed ONLY against detail data FRESHER than the commit
  // that armed it (`armedAt`): the commit invalidates BOTH the workflow detail
  // and the currently-open version query, and whichever refetch lands first
  // re-runs hydration — consuming the reveal against the STALE snapshot left
  // an already-open editor stuck on the PREVIOUS draft (live bug). On consume
  // the target version id moves to a REF the hydration reads for the stagger
  // animation, so intermediate hydrations can't eat the signal.
  const odinoReveal = useOdinoBuild((s) => s.reveal);
  const pendingRevealVersionRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const wf = wfQuery.data;
    if (!wf) return;
    const versions = wf.recent_versions ?? [];
    if (!versions.length) return;
    const newest = versions.reduce((a, b) => (b.version > a.version ? b : a));
    if (!wf.active_version_id && loadedVersionId === null) {
      setLoadedVersionId(newest.id);
    }
    const fresh =
      odinoReveal?.plane === "workflow" &&
      odinoReveal.id === workflowId &&
      wfQuery.dataUpdatedAt > odinoReveal.armedAt;
    if (fresh && newest.id !== wf.active_version_id) {
      setLoadedVersionId(newest.id);
      pendingRevealVersionRef.current = newest.id;
      useOdinoBuild.getState().clearReveal();
    }
  }, [wfQuery.data, wfQuery.dataUpdatedAt, odinoReveal, workflowId, loadedVersionId]);

  // ADR-0008 PR3b3b — load every sub-flow whose id appears as a
  // linked_workflow_id on any sink branch. We scan the CURRENTLY
  // LOADED definition (active or explicit) so adding a new linked
  // sub-flow triggers a fresh query without a page reload.
  const linkedSubflowIds = React.useMemo<string[]>(() => {
    const def =
      loadedVersionId !== null &&
      loadedVersionId !== wfQuery.data?.active_version_id &&
      explicitVersionQ.data
        ? explicitVersionQ.data.definition
        : wfQuery.data?.active_version?.definition;
    if (!def) return [];
    const ids = new Set<string>();
    for (const link of extractSinkBranchLinks(def as unknown as WorkflowDefinition)) {
      if (link.linkedWorkflowId) ids.add(link.linkedWorkflowId);
    }
    return [...ids].sort();
  }, [
    wfQuery.data?.active_version?.definition,
    wfQuery.data?.active_version_id,
    explicitVersionQ.data,
    loadedVersionId,
  ]);

  // Parallel fetch — one query per linked sub-flow. We hit the same
  // ``workflowsApi.get`` the main editor uses, so the active version
  // definition arrives cached + invalidated alongside the workflows
  // detail cache (no separate invalidation strategy needed).
  const subflowDetailQueries = useQueries({
    queries: linkedSubflowIds.map((id) => ({
      queryKey: ["workflow", id],
      queryFn: () => workflowsApi.get(id),
      // Long staleTime — sub-flow defs rarely change while the parent
      // editor is open. The auto-save flow invalidates this key
      // explicitly when a sub-flow is created.
      staleTime: 60_000,
    })),
  });

  // ADR-0008 PR3b3b — stable serialisation of every sub-flow detail
  // query state. We can't depend on the array itself in useEffect
  // (the reference changes every render); a JSON projection of
  // {id, status, data fingerprint} gives us a real dep that flips
  // only when something meaningful changed.
  const subflowQueriesFingerprint = React.useMemo(
    () =>
      JSON.stringify(
        subflowDetailQueries.map((q, i) => ({
          id: linkedSubflowIds[i],
          status: q.status,
          activeVersionId: q.data?.active_version_id ?? null,
        })),
      ),
    [subflowDetailQueries, linkedSubflowIds],
  );

  React.useEffect(() => {
    if (!wfQuery.data || !catalogQuery.data) return;
    // Pick the right definition to hydrate from.
    let def = wfQuery.data.active_version?.definition;
    if (
      loadedVersionId !== null &&
      loadedVersionId !== wfQuery.data.active_version_id &&
      explicitVersionQ.data
    ) {
      def = explicitVersionQ.data.definition;
    }
    if (def) {
      // Seed the on-failure handler from the loaded definition (ADR-0065 T1-4).
      setErrorWorkflowId(def.error_workflow_id ?? null);
      // Many workflows are seeded / imported without UI positions —
      // auto-layout collapses everything at (0,0) which makes the
      // canvas look empty (nodes stack on top of each other).
      const laidOut = autoLayoutIfNeeded(def.nodes, def.edges);
      const flowNodes = laidOut.map((n) => trayNodeToFlow(n, catalogIdx));
      const flowEdges = def.edges.map(trayEdgeToFlow);

      // ADR-0008 PR3b3b — splat each loaded sub-flow into the canvas
      // as a "region" group node + its child sub-flow nodes (prefixed
      // ids to avoid collision with the main flow + so save-time
      // serialisation can route them back to the right WorkflowDef).
      //
      // Default region position: 240px below the parent sink, in a
      // staggered column to the right so multiple regions don't
      // overlap on first load. Operator can drag them anywhere
      // afterwards (positions are session-local in this PR — not
      // persisted backend-side).
      const subflowGroupNodes: Node<NodeData | SubflowRegionNodeData>[] = [];
      const subflowChildNodes: Node<NodeData>[] = [];
      const subflowChildEdges: Edge[] = [];
      let regionIdx = 0;
      for (let i = 0; i < linkedSubflowIds.length; i++) {
        const sfId = linkedSubflowIds[i];
        const q = subflowDetailQueries[i];
        if (!q || q.status !== "success" || !q.data) continue;
        const sfWf = q.data;
        const sfDef = sfWf.active_version?.definition;
        // Find which sink branch in the MAIN def is wired here, to
        // tag the region header.
        const link = extractSinkBranchLinks(def as unknown as WorkflowDefinition).find(
          (l) => l.linkedWorkflowId === sfId,
        );
        const parentBranchId = link?.branchId ?? "—";
        const parentSinkNodeId = link?.sinkNodeId ?? null;

        // Region position priority:
        //   1. operator-dragged position persisted on the sink branch
        //      (SinkBranch.region_position) — survives save/reload.
        //   2. computed default: anchored under the parent sink when
        //      present, else a stagger column on the right.
        const parentSinkPos = parentSinkNodeId
          ? flowNodes.find((n) => n.id === parentSinkNodeId)?.position
          : undefined;
        const computedPos = parentSinkPos
          ? {
              x: parentSinkPos.x + 320,
              y: parentSinkPos.y + regionIdx * 320,
            }
          : { x: 600 + regionIdx * 540, y: 320 };
        const defaultPos = link?.regionPosition ?? computedPos;

        if (!sfDef) {
          // Workflow disabled or no active version — render the
          // wrapper with a warning chip; no children.
          subflowGroupNodes.push({
            id: subflowGroupNodeId(sfId),
            type: "subflowRegion",
            position: defaultPos,
            data: {
              workflowId: sfId,
              label: sfWf.name ?? sfId.slice(0, 8),
              parentBranchId,
              hasNoActiveVersion: true,
            } as SubflowRegionNodeData,
            style: { width: 480, height: 200 },
            // Z-order: regions sit BELOW their children so handles
            // remain interactive even when the cursor crosses the
            // region body.
            zIndex: -1,
          });
          regionIdx++;
          continue;
        }
        const slice = buildSubflowCanvasSlice(sfId, sfDef as unknown as WorkflowDefinition, {
          defaultPosition: defaultPos,
          label: sfWf.name ?? sfId.slice(0, 8),
          parentSinkNodeId,
          parentBranchId,
        });
        subflowGroupNodes.push({
          id: slice.group.id,
          type: "subflowRegion",
          position: slice.group.position,
          data: {
            workflowId: slice.group.workflowId,
            label: slice.group.label,
            parentBranchId: slice.group.parentBranchId ?? "—",
            onDelete: () => deleteSubflowRef.current(slice.group.workflowId),
          } as SubflowRegionNodeData,
          style: { width: slice.group.width, height: slice.group.height },
          zIndex: -1,
        });
        // Children: positions are interpreted RELATIVE to the parent
        // region (React Flow convention with parentNode + extent).
        // The delivery_trigger source is never deletable on its own —
        // removing it would orphan the sub-flow's whole graph. The
        // operator has to delete the entire region (via the trash
        // icon on the wrapper) to drop a sub-flow.
        let triggerChildId: string | null = null;
        for (const child of slice.nodes) {
          const flowChild = {
            ...trayNodeToFlow(child, catalogIdx),
            parentNode: slice.group.id,
            extent: "parent" as const,
          };
          if (child.kind === "delivery_trigger") {
            triggerChildId = flowChild.id;
            // ReactFlow honours node.deletable=false → the Backspace /
            // Delete shortcut + canvas selection won't drop it. The
            // node still selects + opens the NodeEditorDrawer.
            (flowChild as Node<NodeData> & { deletable?: boolean }).deletable = false;
            // ADR-0008 PR3b3c — migrate older sub-flows whose trigger
            // is still at (0,0). That position put the node flush
            // against the region's top-left corner, overlapping the
            // absolute-positioned header chip. Newer sub-flows are
            // seeded at (40, 40) directly by the backend; this client
            // patch only kicks in for ones created before that fix
            // (next save will persist the new position).
            if (flowChild.position.x === 0 && flowChild.position.y === 0) {
              flowChild.position = { x: 40, y: 40 };
            }
          }
          subflowChildNodes.push(flowChild);
        }
        for (const e of slice.edges) {
          subflowChildEdges.push(trayEdgeToFlow(e));
        }

        // ADR-0008 PR3b3b — virtual edge from the parent sink's
        // branch handle to the sub-flow's delivery_trigger node.
        // This edge has no WorkflowEdge counterpart on either
        // definition (the link is captured by
        // SinkBranch.linked_workflow_id, not by a graph edge) — it's
        // a pure visual connector. The dirty / save filters drop it
        // by id prefix (``sf-link:``) so it never leaks into either
        // persisted def.
        if (parentSinkNodeId && triggerChildId) {
          subflowChildEdges.push({
            id: `sf-link:${sfId}`,
            source: parentSinkNodeId,
            sourceHandle: parentBranchId,
            target: triggerChildId,
            // Visually distinct from regular workflow edges — dashed
            // saturated emerald, thick stroke. The earlier 0.6-opacity
            // 1.5px stroke vanished against the dot-pattern canvas
            // background; the user couldn't see the link at all.
            // Bumping it to full opacity + 2.5px + a longer dash so
            // the connection between the sink branch handle and the
            // sub-flow's delivery trigger is unmistakable.
            style: {
              strokeDasharray: "6 4",
              stroke: "rgb(52 211 153)", // emerald-400, full opacity
              strokeWidth: 2.5,
            },
            // Render the virtual link above regular edges so it isn't
            // hidden behind the region (which sits at zIndex -1 with
            // its children above) or the dot-pattern canvas.
            zIndex: 5,
            // No custom edge type → uses the default bezier renderer
            // (no `+` / trash overlay). Explicitly non-deletable so
            // Backspace / canvas delete won't drop it; the only way
            // to remove the link is the trash icon on the wrapper.
            deletable: false,
            // Mark as "subflow-link" so future code (e.g. focus mode
            // highlight, save filters) can find it cheaply.
            data: { subflowLink: true },
          });
        }
        regionIdx++;
      }

      // Combine — IMPORTANT: parent group nodes MUST appear before
      // their children in the React Flow nodes array, otherwise
      // React Flow throws "Couldn't find parent for X" warnings.
      const allNodes = [...flowNodes, ...subflowGroupNodes, ...subflowChildNodes];
      const allEdges = [...flowEdges, ...subflowChildEdges];

      // Draft-first reveal (Odino): stagger the nodes in ("the flow draws
      // itself") ONLY when this hydration renders the exact draft version the
      // auto-load effect just switched to (the pending ref). Gating on the
      // version id (not the store) means an intermediate hydration of the OLD
      // draft — triggered by the commit's own invalidation refetches — can't
      // consume the animation.
      const pendingReveal = pendingRevealVersionRef.current;
      if (
        pendingReveal !== null &&
        loadedVersionId === pendingReveal &&
        explicitVersionQ.data?.id === pendingReveal
      ) {
        allNodes.forEach((n, i) => {
          n.data = { ...(n.data as NodeData), revealDelay: Math.min(i, 24) * 110 };
        });
        pendingRevealVersionRef.current = null;
      }

      setNodes(allNodes as Node<NodeData>[]);
      setEdges(allEdges);
      idCounterRef.current = def.nodes.length;
      // Snapshot MUST go through flowToTray, the same normalizer
      // dirty-detection uses on every render. Going directly from the
      // raw API shape would diff against (a) auto-layouted positions
      // and (b) the default-filled keys (`label: null`, `config: {}`,
      // `source_handle: null`) that trayNodeToFlow / trayEdgeToFlow
      // inject — JSON.stringify keeps explicit nulls but drops
      // undefineds, so the round-trip would always look modified.
      //
      // Sub-flow nodes/edges + the group wrapper are EXCLUDED from
      // the snapshot — they aren't part of the main workflow's
      // definition (they live in their own WorkflowDefinitions).
      // The snapshot stays comparable with what flowToTray returns
      // when we strip sub-flow nodes in the dirty check below.
      const snap = flowToTray(flowNodes, flowEdges);
      setLoadedSnapshot(
        JSON.stringify({
          n: snap.nodes,
          e: snap.edges,
          ew: def.error_workflow_id ?? null,
        }),
      );

      // ADR-0008 PR3b3c — snapshot each loaded sub-flow's definition
      // (un-prefixed ids + relative positions) so the dirty-detection
      // memo can diff the live canvas against it on every render and
      // the N-PATCH save loop only POSTs the sub-flows that actually
      // changed. Use the same extractor the save path will use, so
      // both sides agree on shape.
      const sfSnapshots: Record<string, string> = {};
      for (const sfId of linkedSubflowIds) {
        const slice = extractSubflowSliceFromCanvas(sfId, subflowChildNodes, subflowChildEdges);
        sfSnapshots[sfId] = JSON.stringify({ n: slice.nodes, e: slice.edges });
      }
      setLoadedSubflowSnapshots(sfSnapshots);

      // Fresh hydration = fresh undo context. Past edits on a different
      // version aren't meaningful here.
      setPast([]);
      setFuture([]);
    } else {
      setNodes([]);
      setEdges([]);
      idCounterRef.current = 0;
      setLoadedSnapshot("");
      setLoadedSubflowSnapshots({});
    }
  }, [
    wfQuery.data,
    catalogQuery.data,
    catalogIdx,
    setNodes,
    setEdges,
    loadedVersionId,
    explicitVersionQ.data,
    // Sub-flow splat depends on the fingerprint, not the raw queries
    // array (which changes reference every render).
    subflowQueriesFingerprint,
    linkedSubflowIds,
    // Discard bumps this to re-run hydration (main + sub-flow regions)
    // even when nothing else changed.
    hydrationNonce,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  // ADR-0008 PR3b3b — sub-flow regions live ON the canvas (group
  // wrapper + child nodes prefixed with ``sf:<workflow_id>:``) but
  // belong to OTHER workflow definitions. Filter them out before
  // dirty detection and before save serialisation so they don't
  // pollute the main workflow's nodes/edges array.
  //
  // The wrapper node id is ``subflow:<workflow_id>``; child nodes
  // carry ``parentNode`` set to that. Edges between sub-flow nodes
  // have prefixed ids. The two filters below drop both.
  // Both derive from the single tested partition helper
  // (splitMainFromSubflowCanvas) so the serialisation-boundary filter
  // has one source of truth — see subflowOrchestration.ts.
  const mainCanvasNodes = React.useMemo(
    () => splitMainFromSubflowCanvas(nodes, edges).mainNodes,
    [nodes, edges],
  );
  const mainCanvasEdges = React.useMemo(
    () => splitMainFromSubflowCanvas(nodes, edges).mainEdges,
    [nodes, edges],
  );

  // Dirty detection: serialise the current canvas in the same shape we
  // snapshot at load. Cheap enough on every render given the editor
  // is bounded by graph size.
  const isMainModified = React.useMemo(() => {
    if (!loadedSnapshot) return false;
    const { nodes: trayNodes, edges: trayEdges } = flowToTray(mainCanvasNodes, mainCanvasEdges);
    return JSON.stringify({ n: trayNodes, e: trayEdges, ew: errorWorkflowId }) !== loadedSnapshot;
  }, [mainCanvasNodes, mainCanvasEdges, loadedSnapshot, errorWorkflowId]);

  // ADR-0008 PR3b3c — per-sub-flow dirty map. Compares the live canvas
  // slice (un-prefixed ids + relative positions) against the snapshot
  // captured at hydration. Returns the set of sub-flow workflow ids
  // that have diverged.
  const dirtySubflowIds = React.useMemo<string[]>(
    () => computeDirtySubflowIds(loadedSubflowSnapshots, nodes, edges),
    [nodes, edges, loadedSubflowSnapshots],
  );

  // Combined dirty flag: the Save button + unsaved-changes banner light
  // up when either the main canvas OR any linked sub-flow has changes.
  const isModified = isMainModified || dirtySubflowIds.length > 0;

  // ---------- Undo / Redo --------------------------------------------------
  //
  // Two stacks of normalized graph snapshots (JSON strings via flowToTray
  // — same shape dirty-detection uses, so undoing back to the loaded
  // baseline correctly flips isModified back to false).
  //
  // Push BEFORE every mutation so the stack records pre-mutation states.
  // Mutation sites that commit: onNodeDragStart (whole drag = 1 entry),
  // onConnect, onDrop, duplicateNode, deleteNode, patchNode (config commit
  // from drawer), and edge-removal intercepted in onEdgesChange.
  //
  // Skip: selection-only changes, dimension changes, drag-in-progress
  // position events (covered by the drag-START snapshot), inspector
  // keystrokes (drawer commits in one shot via patchNode).
  const HISTORY_MAX = 50;
  // A history entry captures the WHOLE canvas — main flow PLUS the
  // sub-flow region wrappers + their parented children + the virtual
  // ``sf-link:`` edges. We store the live ReactFlow node/edge ARRAY
  // references (not a JSON projection): ReactFlow updates state
  // immutably (applyNodeChanges / setNodes always produce fresh
  // objects), so a captured reference stays a faithful, frozen snapshot
  // even as the canvas mutates afterwards. This is the ONLY way undo can
  // restore region nodes losslessly — round-tripping them through the
  // tray<->flow normaliser drops ``parentNode`` / ``extent`` / ``type`` /
  // ``style`` and the region wrapper's ``data`` (incl. its ``onDelete``
  // closure), which is exactly what used to corrupt sub-flows on undo.
  type GraphSnapshot = { nodes: Node<NodeData>[]; edges: Edge[] };
  const [past, setPast] = React.useState<GraphSnapshot[]>([]);
  const [future, setFuture] = React.useState<GraphSnapshot[]>([]);
  const applyingHistoryRef = React.useRef(false);

  const snapshotGraph = React.useCallback((): GraphSnapshot => ({ nodes, edges }), [nodes, edges]);

  const pushHistory = React.useCallback(() => {
    // While applying an undo/redo we MUST NOT push — would poison the
    // stack and break the redo direction.
    if (applyingHistoryRef.current) return;
    const snap = snapshotGraph();
    setPast((p) => {
      // Dedup when nothing actually changed since the last push — the
      // array references are identical when no setNodes/setEdges ran in
      // between (e.g. a drag gesture that didn't move anything).
      if (
        p.length > 0 &&
        p[p.length - 1].nodes === snap.nodes &&
        p[p.length - 1].edges === snap.edges
      ) {
        return p;
      }
      const next = [...p, snap];
      // Bounded — drop the oldest so a long session doesn't blow memory
      // on a workflow with hundreds of nodes.
      if (next.length > HISTORY_MAX) next.shift();
      return next;
    });
    // Any new edit invalidates the redo path — that's standard
    // undo-tree semantics; we don't try to be clever.
    setFuture([]);
  }, [snapshotGraph]);

  const applySnapshot = React.useCallback(
    (snap: GraphSnapshot) => {
      applyingHistoryRef.current = true;
      // Restore the exact captured arrays — full fidelity, including the
      // sub-flow regions. No normalisation, no partition: the snapshot IS
      // the canvas as it was.
      setNodes(snap.nodes);
      setEdges(snap.edges);
      // Drop the flag on the next tick so any setState reactions
      // triggered by setNodes/setEdges don't spuriously push.
      queueMicrotask(() => {
        applyingHistoryRef.current = false;
      });
    },
    [setNodes, setEdges],
  );

  // Undoing/redoing an EDIT means the user is back to authoring — drop any
  // lingering test-run overlay (green/animated traversed edges + status rings)
  // so the restored snapshot renders as a clean graph instead of the historical
  // shape fighting the last run's animated edges (looked like "the canvas
  // animates on undo"). Exit live mode too so nothing re-lights afterwards.
  const clearRunOverlay = React.useCallback(() => {
    armedForLiveRef.current = false;
    runCancelledRef.current = true;
    setLiveExecutionId(null);
    setTraceStatus({});
    setNodeProgress({});
    setNodeLiveCount({});
    liveCountRef.current = newLiveCountState();
  }, []);

  const undo = React.useCallback(() => {
    setPast((p) => {
      if (p.length === 0) return p;
      const prev = p[p.length - 1];
      const curr = snapshotGraph();
      setFuture((f) => [...f, curr]);
      clearRunOverlay();
      applySnapshot(prev);
      return p.slice(0, -1);
    });
  }, [snapshotGraph, applySnapshot, clearRunOverlay]);

  const redo = React.useCallback(() => {
    setFuture((f) => {
      if (f.length === 0) return f;
      const next = f[f.length - 1];
      const curr = snapshotGraph();
      setPast((p) => [...p, curr]);
      clearRunOverlay();
      applySnapshot(next);
      return f.slice(0, -1);
    });
  }, [snapshotGraph, applySnapshot, clearRunOverlay]);

  const clearHistory = React.useCallback(() => {
    setPast([]);
    setFuture([]);
  }, []);

  const onConnect: OnConnect = React.useCallback(
    (params: Connection) => {
      // Defensive: also enforced by isValidConnection below, but a
      // second check costs nothing and keeps a stale state from
      // sneaking past.
      if (params.source && params.target && params.source === params.target) {
        return;
      }
      pushHistory();
      const id = `e_${params.source}_${params.target}_${Date.now()}`;
      setEdges((eds) => addEdge({ ...params, id }, eds));
    },
    [setEdges, pushHistory],
  );

  /** Block obvious garbage at connection time (before the user releases
   *  the mouse): self-loops, edges in/out of source/sink wrong-direction,
   *  and edges that duplicate an existing connection. ReactFlow calls
   *  this during the drag and grays out illegal drop targets. */
  const isValidConnection = React.useCallback(
    (conn: Connection): boolean => {
      if (!conn.source || !conn.target) return false;
      if (conn.source === conn.target) return false;
      const srcNode = nodes.find((n) => n.id === conn.source);
      const tgtNode = nodes.find((n) => n.id === conn.target);
      if (srcNode?.data.spec?.category === "sink") return false;
      // Nothing connects INTO an entry point (trigger or read_canonical) —
      // it has no input, a workflow starts there.
      if (tgtNode && isEntrypointKind(tgtNode.data.kind, tgtNode.data.spec?.category)) return false;
      // The graph is a DAG — reject any edge that would close a cycle
      // (connecting a node to one of its own ancestors). The ONE exception:
      // the `loop` (Split-In-Batches) cycle. Two halves, BOTH needed:
      //   (a) the candidate itself may target a loop node (the back-edge);
      //   (b) EXISTING back-edges must be STRIPPED before the check — the
      //       same graph-wide strip validateGraph + the backend Kahn do.
      // Without (b), once the back-edge (body→loop) exists, the FORWARD
      // edge loop→body reads as closing a cycle THROUGH the loop and the
      // Loop handle refuses every connection (the "non riesco a collegare
      // l'handle Loop" bug).
      const loopIds = new Set(
        nodes.filter((n) => n.data.kind === "loop").map((n) => n.id),
      );
      const dagEdges = edges.filter((e) => !loopIds.has(e.target));
      if (tgtNode?.data.kind !== "loop" && wouldCreateCycle(dagEdges, conn.source, conn.target))
        return false;
      // Reject duplicate (same source+source_handle+target)
      const dup = edges.some(
        (e) =>
          e.source === conn.source &&
          e.target === conn.target &&
          (e.sourceHandle ?? null) === (conn.sourceHandle ?? null),
      );
      if (dup) return false;
      return true;
    },
    [nodes, edges],
  );

  // Validation issues for the current canvas — recomputed on any
  // node/edge change. Errors block save; warnings are informational.
  const graphIssues = React.useMemo(() => validateGraph(nodes, edges), [nodes, edges]);
  const graphErrors = graphIssues.filter((i) => i.severity === "error");
  const graphWarnings = graphIssues.filter((i) => i.severity === "warning");
  // The issues panel is COLLAPSED to a small chip by default so its full
  // width doesn't cover the top-right node's "+" add-node affordance. Errors
  // auto-expand (they block save, the user must see them); warnings stay a
  // chip until clicked.
  const [issuesPanelOpen, setIssuesPanelOpen] = React.useState(false);

  const onDragOver = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  // ─── N8N-style "+" on edges: insert a node mid-edge ─────────────────
  // The popover is rendered by the editor (not the edge component) so
  // it floats above ReactFlow's canvas; the edge fires onInsertClick
  // with its id + the cursor screen position, the editor stashes
  // those in state, renders the popover, and on selection performs:
  //
  //   1. Snapshot for undo.
  //   2. Compute the new node's position at the midpoint of the edge's
  //      source/target node centers (rough but predictable; the user
  //      can drag it after).
  //   3. Create the node (same shape as onDrop).
  //   4. Replace the original edge with two new edges (src→new, new→tgt).
  //
  // ``edgeInsert`` is null when no popover is open.
  const [edgeInsert, setEdgeInsert] = React.useState<{
    edgeId: string;
    screenPos: { x: number; y: number };
  } | null>(null);

  const insertNodeOnEdge = React.useCallback(
    (edgeId: string, kind: NodeKind) => {
      const spec = catalogIdx[kind];
      if (!spec) return;
      const edge = edges.find((e) => e.id === edgeId);
      if (!edge) return;
      const srcNode = nodes.find((n) => n.id === edge.source);
      const tgtNode = nodes.find((n) => n.id === edge.target);
      if (!srcNode || !tgtNode) return;

      // Placement. For a FORWARD edge the midpoint sits the new node neatly
      // between source and target. But for a LOOP BACK-EDGE (the body edging
      // back into the loop node — target is a `loop` OR simply lands LEFT of
      // the source) the raw midpoint falls in a nonsensical spot (the target
      // is far left), so the two resulting edges both read as tangled
      // returns. Instead CONTINUE THE BODY ROW: drop the node just to the
      // RIGHT of the source at the source's height, so `source→new` stays a
      // clean forward edge and `new→loop` is the single tidy back-edge routed
      // below — n8n's loop layout, no manual dragging.
      const isBackEdgeInsert =
        tgtNode.data.kind === "loop" || tgtNode.position.x < srcNode.position.x - 8;
      const midX = isBackEdgeInsert
        ? srcNode.position.x + 280
        : (srcNode.position.x + tgtNode.position.x) / 2;
      const midY = isBackEdgeInsert
        ? srcNode.position.y
        : (srcNode.position.y + tgtNode.position.y) / 2;

      idCounterRef.current += 1;
      const newId = `${kind}_${idCounterRef.current}`;

      const seedConfig: Record<string, unknown> = {};
      const props =
        (spec.config_schema as { properties?: Record<string, { default?: unknown }> }).properties ??
        {};
      for (const [k, v] of Object.entries(props)) {
        if (v.default !== undefined) seedConfig[k] = v.default;
      }
      const newNode: Node<NodeData> = {
        id: newId,
        type: "tray",
        position: { x: midX, y: midY },
        data: { kind, label: null, config: seedConfig, spec },
      };

      pushHistory();
      setNodes((nds) => nds.concat(newNode));
      setEdges((eds) => {
        const others = eds.filter((e) => e.id !== edgeId);
        // Preserve the source handle on the upstream half — important
        // for IF/SWITCH outputs where the handle id carries the
        // routing decision. The downstream half is plain (default
        // handle on the new node).
        const e1: Edge = {
          id: `${edgeId}__upstream_${newId}`,
          source: edge.source,
          target: newId,
          sourceHandle: edge.sourceHandle,
          type: "trayActions",
        };
        const e2: Edge = {
          id: `${edgeId}__downstream_${newId}`,
          source: newId,
          target: edge.target,
          type: "trayActions",
        };
        return [...others, e1, e2];
      });
      setEdgeInsert(null);
    },
    [edges, nodes, catalogIdx, setNodes, setEdges, pushHistory],
  );

  const deleteEdgeById = React.useCallback(
    (edgeId: string) => {
      pushHistory();
      setEdges((eds) => eds.filter((e) => e.id !== edgeId));
    },
    [setEdges, pushHistory],
  );

  // N8N-style "extend the flow" — fired when the user clicks the "+"
  // floating off the right side of a leaf node. Insert a new node
  // ~280px to the right of the source + create a single edge from
  // the source's default handle into the new node's input.
  //
  // ADR-0008 PR3b3c — if the source is INSIDE a sub-flow region (has
  // ``parentNode`` set), the new node inherits the parent and uses
  // the ``sf:<wfId>:`` id prefix so save-time extraction routes it
  // back to the right WorkflowDefinition. The position offset is
  // still relative to the parent region (React Flow's extent="parent"
  // convention) — no transform needed.
  const addNodeAfter = React.useCallback(
    (sourceNodeId: string, kind: NodeKind) => {
      const spec = catalogIdx[kind];
      if (!spec) return;
      const src = nodes.find((n) => n.id === sourceNodeId);
      if (!src) return;

      // Detect: is the source a sub-flow child? If so, build prefixed
      // ids for the new node + edge so save-time extraction picks them
      // up. Otherwise: regular main-flow node.
      const parentGroupId = src.parentNode;
      const sfWfId = parentGroupId ? workflowIdFromGroupId(parentGroupId) : null;

      idCounterRef.current += 1;
      const baseId = `${kind}_${idCounterRef.current}`;
      const newId = sfWfId ? subflowChildNodeId(sfWfId, baseId) : baseId;
      const seedConfig: Record<string, unknown> = {};
      const props =
        (spec.config_schema as { properties?: Record<string, { default?: unknown }> }).properties ??
        {};
      for (const [k, v] of Object.entries(props)) {
        if (v.default !== undefined) seedConfig[k] = v.default;
      }
      const newNode: Node<NodeData> = {
        id: newId,
        type: "tray",
        position: { x: src.position.x + 280, y: src.position.y },
        data: { kind, label: null, config: seedConfig, spec },
        ...(sfWfId && parentGroupId
          ? { parentNode: parentGroupId, extent: "parent" as const }
          : {}),
      };
      // Edge id is prefixed when the source is a sub-flow child, so
      // both ends of the edge live in the same prefix namespace and
      // ``extractSubflowSliceFromCanvas`` strips them as a pair. The
      // un-prefixed edge id keeps human-readable ``e_<src>_<tgt>``
      // form using the LOCAL ids (so the resulting WorkflowEdge in
      // the sub-flow def reads ``e_<local-src>_<local-tgt>``).
      const childPrefix = sfWfId ? `sf:${sfWfId}:` : "";
      const srcLocal =
        sfWfId && sourceNodeId.startsWith(childPrefix)
          ? sourceNodeId.slice(childPrefix.length)
          : sourceNodeId;
      const edgeId = sfWfId
        ? subflowChildEdgeId(sfWfId, `e_${srcLocal}_${baseId}`)
        : `e_${sourceNodeId}_${newId}`;
      pushHistory();
      setNodes((nds) => nds.concat(newNode));
      setEdges((eds) =>
        eds.concat({
          id: edgeId,
          source: sourceNodeId,
          target: newId,
          type: "trayActions",
        } as Edge),
      );
      setEdgeInsert(null); // close popover if open
    },
    [catalogIdx, nodes, setNodes, setEdges, pushHistory],
  );

  // ``leafAddState`` — when the user clicks the "+" on a leaf node,
  // we stash the source node id + click position so the popover can
  // open at the cursor and route the selection through ``addNodeAfter``.
  // Lives separately from ``edgeInsert`` so the two flows don't get
  // tangled — the popover renders whichever one is active.
  const [leafAdd, setLeafAdd] = React.useState<{
    sourceId: string;
    screenPos: { x: number; y: number };
  } | null>(null);

  // ``watchSubflowId`` — the child workflow id whose read-only drill-in
  // is open, launched by the "⤢" corner button on a ``call_workflow``
  // node. Overlays the child LIVE during a run (child SSE rides the
  // parent ``liveExecutionId``); works before/while running too.
  const [watchSubflowId, setWatchSubflowId] = React.useState<string | null>(null);

  // ``paletteDrawerOpen`` — the right-side categorised drawer opened
  // by the floating top-right "+" button. Different surface from the
  // contextual edge / leaf popovers (those stay quick + small at the
  // cursor). The drawer is for "I want to add a node, surprise me".
  // Selection drops the new node at the visible viewport center via
  // ``addNodeStandalone``.
  const [paletteDrawerOpen, setPaletteDrawerOpen] = React.useState(false);

  const addNodeStandalone = React.useCallback(
    (kind: NodeKind) => {
      const spec = catalogIdx[kind];
      if (!spec) return;
      // Drop at the visible viewport center — the drawer is on the
      // right side so we bias the position slightly LEFT of the
      // geometric centre to avoid the node landing under the drawer.
      const bounds = wrapperRef.current?.getBoundingClientRect();
      const cx = (bounds?.width ?? 1200) / 2 - 192; // 192 = ~half drawer width
      const cy = (bounds?.height ?? 800) / 2;
      const flowPos = rf.project({ x: cx, y: cy });

      idCounterRef.current += 1;
      const newId = `${kind}_${idCounterRef.current}`;
      const seedConfig: Record<string, unknown> = {};
      const props =
        (spec.config_schema as { properties?: Record<string, { default?: unknown }> }).properties ??
        {};
      for (const [k, v] of Object.entries(props)) {
        if (v.default !== undefined) seedConfig[k] = v.default;
      }
      const newNode: Node<NodeData> = {
        id: newId,
        type: "tray",
        position: flowPos,
        data: { kind, label: null, config: seedConfig, spec },
      };
      pushHistory();
      setNodes((nds) => nds.concat(newNode));
      setSelectedId(newId);
      setPaletteDrawerOpen(false);
    },
    [catalogIdx, rf, setNodes, pushHistory],
  );

  // ADR-0008 PR3b3 — sub-flow create-on-branch state. Tracks which
  // (nodeId, branchId) is currently being created so the row can
  // show a spinner + disable double-clicks. The actual mutation
  // handler is declared further down (it depends on saveMutation
  // which is declared after this section in the file order).
  const [creatingSubflowFor, setCreatingSubflowFor] = React.useState<{
    nodeId: string;
    branchId: string;
  } | null>(null);

  // ADR-0008 PR3b3c — keep each sub-flow region's perimeter sized to
  // fit its children. The wrapper starts at a default size at
  // hydration (~480×280) and the operator can drop nodes inside via
  // the leaf + button; if the new child overflows the perimeter
  // ReactFlow's extent="parent" silently clamps it back to the
  // wrapper bounds — visually it looks pinned to an invisible wall.
  //
  // We compute the children's bounding box every render and patch the
  // wrapper's style.width / .height upward (never shrink below the
  // default — the operator can resize down by deleting the children
  // first, which is rare). The setNodes call is guarded by a value
  // diff to avoid an infinite loop with React Flow's own width/height
  // measurement callbacks.
  //
  // Sub-pixel rounding to whole pixels keeps the diff stable: React
  // Flow returns fractional dimensions from getBoundingClientRect()
  // and a 0.5px wobble would re-trigger setNodes forever.
  const SUBFLOW_REGION_PAD_X = 48;
  const SUBFLOW_REGION_PAD_Y = 56;
  const SUBFLOW_REGION_MIN_W = 480;
  // Matches the default region height set at hydration; auto-grow
  // never shrinks below this so a fresh sub-flow has breathing room.
  const SUBFLOW_REGION_MIN_H = 280;
  React.useEffect(() => {
    let dirty = false;
    const next = nodes.map((n) => {
      if (n.type !== "subflowRegion") return n;
      const children = nodes.filter((c) => c.parentNode === n.id);
      if (children.length === 0) return n;
      let maxX = 0;
      let maxY = 0;
      for (const c of children) {
        // React Flow populates ``width`` / ``height`` after measuring
        // the child's DOM box. Before the first measurement we fall
        // back to typical TrayNodeBox dimensions so the region still
        // starts at a sane size.
        const w = c.width ?? 240;
        const h = c.height ?? 80;
        maxX = Math.max(maxX, c.position.x + w);
        maxY = Math.max(maxY, c.position.y + h);
      }
      const wantedW = Math.max(SUBFLOW_REGION_MIN_W, Math.round(maxX + SUBFLOW_REGION_PAD_X));
      const wantedH = Math.max(SUBFLOW_REGION_MIN_H, Math.round(maxY + SUBFLOW_REGION_PAD_Y));
      const currStyle = (n.style ?? {}) as React.CSSProperties;
      const currW = Number(currStyle.width ?? 0);
      const currH = Number(currStyle.height ?? 0);
      if (currW === wantedW && currH === wantedH) return n;
      dirty = true;
      return {
        ...n,
        style: { ...currStyle, width: wantedW, height: wantedH },
      };
    });
    if (dirty) setNodes(next);
  }, [nodes, setNodes]);

  // ADR-0008 PR3b3 follow-up — persist where the operator drags a
  // sub-flow region. The region wrapper node is a ``subflowRegion`` that
  // gets filtered OUT of the main definition at save time, so its canvas
  // position would be lost on the next hydration (it reset to the
  // computed default after every save). We mirror the position onto the
  // parent sink's ``config.branches[].region_position`` — that DOES round
  // -trip through the main save + re-hydrate, and the hydration effect
  // reads it back via ``extractSinkBranchLinks(...).regionPosition``.
  //
  // Patching the branch config also marks the main flow dirty, so the
  // Save button lights up after a drag (the move is a real, persistable
  // change). The runtime ignores ``region_position`` — it's pure layout.
  const persistRegionPosition = React.useCallback(
    (subflowId: string, pos: { x: number; y: number }) => {
      const rx = Math.round(pos.x);
      const ry = Math.round(pos.y);
      setNodes((nds) =>
        nds.map((n) => {
          // Only real main-flow sink nodes hold branch links.
          if (n.type === "subflowRegion" || n.parentNode !== undefined || isSubflowChildNode(n)) {
            return n;
          }
          const cfg = (n.data?.config ?? {}) as Record<string, unknown>;
          const branches = cfg.branches as Array<Record<string, unknown>> | undefined;
          if (!branches) return n;
          let touched = false;
          const next = branches.map((b) => {
            if (b?.linked_workflow_id !== subflowId) return b;
            const rp = b.region_position as { x?: number; y?: number } | undefined;
            if (rp && rp.x === rx && rp.y === ry) return b; // no-op
            touched = true;
            return { ...b, region_position: { x: rx, y: ry } };
          });
          if (!touched) return n;
          return { ...n, data: { ...n.data, config: { ...cfg, branches: next } } };
        }),
      );
    },
    [setNodes],
  );

  // Forward-declared ref to the mutation handler — `nodesWithLeaf`
  // below needs to reference it, but the implementation depends on
  // saveMutation which lives below `nodesWithLeaf`. The ref + assign
  // pattern keeps the code order working without restructuring the
  // whole component.
  const createBranchSubflowRef = React.useRef<(nodeId: string, branchId: string) => void>(() => {
    /* assigned after saveMutation is defined */
  });
  // Same forward-declared-ref pattern for the inverse op: delete a
  // sub-flow + unlink the parent branch + auto-save. Used by the
  // SubflowRegionNode trash button.
  const deleteSubflowRef = React.useRef<(subflowId: string) => void>(() => {
    /* assigned after saveMutation is defined */
  });
  // Forward-declared ref for the per-node toolbar actions (run / duplicate /
  // delete). Assigned each render AFTER those handlers are defined, so the
  // ``nodesWithLeaf`` memo below can wire them at call-time without adding
  // them to its deps (mirrors ``createBranchSubflowRef``).
  const nodeActionsRef = React.useRef<{
    run: (id: string) => void;
    duplicate: (id: string) => string | null;
    del: (id: string) => void;
  }>({ run: () => {}, duplicate: () => null, del: () => {} });

  // Nodes shaped for ReactFlow: stamp ``isLeaf`` + ``onAddAfter`` on
  // every node so TrayNodeBox can render its "+" affordance without
  // pulling state from a context. A node is a "leaf" when it's not a
  // sink AND has no outgoing edges in the current canvas. Past-run
  // mode keeps the bare shape (no buttons in read-only view).
  const nodesWithLeaf = React.useMemo<Node<NodeData>[]>(() => {
    if (viewMode === "executions") return nodes;
    const hasOutgoing = new Set(edges.map((e) => e.source));
    return nodes.map((n) => {
      // ADR-0008 PR3b3 — build the per-branch link map for sink nodes.
      // The map covers EVERY handle outputHandlesFor would return for
      // this node (so implicit succeeded / dead_letter always get a
      // `+` even without a user-declared SinkBranch entry).
      let branchSubflowLinks: Record<string, string | null> | undefined;
      if (n.data.spec?.category === "sink") {
        branchSubflowLinks = {};
        const cfg = (n.data.config ?? {}) as Record<string, unknown>;
        // Seed with implicit branches — null until linked.
        branchSubflowLinks.succeeded = null;
        branchSubflowLinks.dead_letter = null;
        // Overlay any user-declared branches (catches custom ones AND
        // overrides implicit entries if the user has wired them via
        // BranchesEditor).
        const branches = (cfg.branches as Array<Record<string, unknown>>) ?? [];
        for (const b of branches) {
          const bid = b?.id as string | undefined;
          if (!bid) continue;
          branchSubflowLinks[bid] = (b?.linked_workflow_id as string | null | undefined) ?? null;
        }
      }
      return {
        ...n,
        data: {
          ...n.data,
          isLeaf: n.data.spec?.category !== "sink" && !hasOutgoing.has(n.id),
          onAddAfter: (sourceId: string, pos: { x: number; y: number }) =>
            setLeafAdd({ sourceId, screenPos: pos }),
          onWatchSubflow: (childWorkflowId: string) => setWatchSubflowId(childWorkflowId),
          branchSubflowLinks,
          onCreateBranchSubflow: branchSubflowLinks
            ? (branchId: string) => createBranchSubflowRef.current(n.id, branchId)
            : undefined,
          onOpenBranchSubflow: branchSubflowLinks
            ? (linkedId: string) => window.open(`/workflows/${linkedId}`, "_blank")
            : undefined,
          creatingSubflowBranchId:
            creatingSubflowFor?.nodeId === n.id ? creatingSubflowFor.branchId : null,
          // N8N-style hover toolbar + context menu. Mutating actions are
          // gated on write permission; Open + Menu are always available.
          onRunNode: canWrite ? (id: string) => nodeActionsRef.current.run(id) : undefined,
          onDuplicateNode: canWrite
            ? (id: string) => nodeActionsRef.current.duplicate(id)
            : undefined,
          onDeleteNode: canWrite ? (id: string) => nodeActionsRef.current.del(id) : undefined,
          onOpenNode: (id: string) => setDrawerNodeId(id),
          onOpenMenu: (id: string, pos: { x: number; y: number }) =>
            setNodeMenu({ id, x: pos.x, y: pos.y }),
        },
      };
    });
    // createBranchSubflowRef / nodeActionsRef are mutated in a later effect
    // / each render, so they aren't deps; the ref's .current pointer is
    // reached at call-time inside the closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, edges, viewMode, creatingSubflowFor, canWrite]);

  // Edges shaped for ReactFlow: stamp the N8N-style action callbacks
  // onto each edge's ``data``. Memoised on edges + the callbacks so
  // ReactFlow doesn't re-render edges on unrelated state changes.
  // Past-run view (``viewMode === "executions"``) gets bare edges
  // with no callbacks — read-only canvas.
  const edgesWithActions = React.useMemo<Edge[]>(() => {
    if (viewMode === "executions") return edges;
    return edges.map((e) => ({
      ...e,
      data: {
        ...((e.data ?? {}) as object),
        onInsertClick: (edgeId: string, pos: { x: number; y: number }) =>
          setEdgeInsert({ edgeId, screenPos: pos }),
        onDelete: deleteEdgeById,
      } satisfies TrayEdgeActionsData,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edges, deleteEdgeById, viewMode]);

  const onDrop = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const kind = e.dataTransfer.getData("application/x-tray-node-kind") as NodeKind;
      if (!kind) return;
      const spec = catalogIdx[kind];
      if (!spec) return;

      const bounds = wrapperRef.current?.getBoundingClientRect();
      const position = rf.project({
        x: e.clientX - (bounds?.left ?? 0),
        y: e.clientY - (bounds?.top ?? 0),
      });

      idCounterRef.current += 1;
      const newId = `${kind}_${idCounterRef.current}`;

      // Seed config with defaults from the JSON Schema where possible.
      const seedConfig: Record<string, unknown> = {};
      const props =
        (spec.config_schema as { properties?: Record<string, { default?: unknown }> }).properties ??
        {};
      for (const [k, v] of Object.entries(props)) {
        if (v.default !== undefined) seedConfig[k] = v.default;
      }

      const newNode: Node<NodeData> = {
        id: newId,
        type: "tray",
        position,
        data: { kind, label: null, config: seedConfig, spec },
      };
      pushHistory();
      setNodes((nds) => nds.concat(newNode));
      setSelectedId(newId);
    },
    [catalogIdx, rf, setNodes, pushHistory],
  );

  function patchNode(id: string, patch: Partial<TrayNode>) {
    // Drawer commits one patch per Save click (label / config / kind), so
    // each call deserves its own undo entry. Dedup in pushHistory swallows
    // no-op patches.
    pushHistory();
    setNodes((nds) =>
      nds.map((n) =>
        n.id === id
          ? {
              ...n,
              data: {
                ...n.data,
                // ADR-0011 — switching an integration Action rewrites the
                // node kind in place (carrying shared config across).
                ...(patch.kind !== undefined ? { kind: patch.kind } : null),
                ...(patch.label !== undefined ? { label: patch.label } : null),
                ...(patch.config !== undefined ? { config: patch.config } : null),
                ...(patch.execute_once !== undefined ? { execute_once: patch.execute_once } : null),
                ...(patch.notes !== undefined ? { notes: patch.notes } : null),
                ...(patch.always_output_data !== undefined
                  ? { always_output_data: patch.always_output_data }
                  : null),
                ...(patch.retry_on_fail !== undefined
                  ? { retry_on_fail: patch.retry_on_fail }
                  : null),
                ...(patch.max_tries !== undefined ? { max_tries: patch.max_tries } : null),
                ...(patch.retry_wait_ms !== undefined
                  ? { retry_wait_ms: patch.retry_wait_ms }
                  : null),
                ...(patch.on_error !== undefined ? { on_error: patch.on_error } : null),
                ...(patch.execution_mode !== undefined
                  ? { execution_mode: patch.execution_mode }
                  : null),
              },
            }
          : n,
      ),
    );
  }

  function deleteNode(id: string) {
    pushHistory();
    setNodes((nds) => nds.filter((n) => n.id !== id));
    setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
    if (selectedId === id) setSelectedId(null);
    if (drawerNodeId === id) setDrawerNodeId(null);
  }

  /** Clone the currently-selected node a few px south-east. Used by the
   *  drawer's Duplicate button and by the Cmd+D keyboard shortcut.
   *  Edges are intentionally NOT duplicated — copies start unwired so
   *  the user explicitly opts in to which connections to bring along. */
  const duplicateNode = React.useCallback(
    (id: string): string | null => {
      const src = nodes.find((n) => n.id === id);
      if (!src) return null;
      idCounterRef.current += 1;
      const newId = `${src.data.kind}_${idCounterRef.current}`;
      const cloned: Node<NodeData> = {
        ...src,
        id: newId,
        position: { x: src.position.x + 40, y: src.position.y + 40 },
        selected: true,
        data: {
          ...src.data,
          // Deep-copy the config to avoid the clone aliasing the
          // original's object identity (mutating one would mutate both).
          config: JSON.parse(JSON.stringify(src.data.config ?? {})),
          label: src.data.label ? `${src.data.label} (copy)` : null,
        },
      };
      pushHistory();
      setNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, selected: false } : n)).concat(cloned),
      );
      setSelectedId(newId);
      return newId;
    },
    [nodes, setNodes, pushHistory],
  );

  // Run a single node from the canvas toolbar / context menu: open its
  // drawer and auto-fire the Execute Node flow (which prompts for trigger
  // input when needed, exactly like clicking the drawer's button).
  const runNodeFromCanvas = React.useCallback((id: string) => {
    setDrawerNodeId(id);
    setAutoRunToken((t) => t + 1);
  }, []);

  // Keep the forward-declared ref pointing at the latest handlers so the
  // ``nodesWithLeaf`` memo (declared above) reads them at call-time.
  nodeActionsRef.current = {
    run: runNodeFromCanvas,
    duplicate: duplicateNode,
    del: deleteNode,
  };

  const toggleEnabledMutation = useMutation({
    mutationFn: (enabled: boolean) => workflowsApi.update(workflowId, { enabled }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workflow", workflowId] });
      qc.invalidateQueries({ queryKey: ["workflows"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => workflowsApi.delete(workflowId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workflows"] });
      // Navigate away from the (now non-existent) workflow.
      window.location.href = "/workflows";
    },
  });

  const navigate = useNavigate();
  const duplicateMutation = useMutation({
    mutationFn: () => workflowsApi.duplicate(workflowId),
    onSuccess: (newWf) => {
      qc.invalidateQueries({ queryKey: ["workflows"] });
      navigate(`/workflows/${newWf.id}`);
    },
    onError: async (err: Error) => {
      // Default-name collision (most common error): ask for a unique
      // name and retry. Anything else: surface the error verbatim.
      if (!wfQuery.data) return;
      const fallback = window.prompt(
        `Couldn't duplicate "${wfQuery.data.name}":\n${err.message}\n\nPick a different name (Cancel to abort):`,
        `${wfQuery.data.name} (copy 2)`,
      );
      if (!fallback) return;
      try {
        const retried = await workflowsApi.duplicate(workflowId, { name: fallback });
        qc.invalidateQueries({ queryKey: ["workflows"] });
        navigate(`/workflows/${retried.id}`);
      } catch (e) {
        window.alert(`Still failed: ${(e as Error).message}`);
      }
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (activate: boolean) => {
      if (!wfQuery.data) throw new Error("workflow not loaded");

      // ADR-0008 PR3b3c — N-PATCH orchestration. The unified canvas
      // hosts the main flow PLUS every linked sub-flow. The Save
      // button has to round-trip BOTH the main definition AND each
      // sub-flow definition that diverged from its snapshot.
      //
      // Order: sub-flows first (so the parent's link integrity is
      // preserved even if the main save races a refresh), then main.
      // If a sub-flow save fails we abort BEFORE touching the parent —
      // a partial save would leave the linked workflow id pointing at
      // a stale def.
      for (const sfId of dirtySubflowIds) {
        const slice = extractSubflowSliceFromCanvas(sfId, nodes, edges);
        // The sub-flow workflow itself is orchestration-kind by
        // construction (POST /sub-flow-for-branch enforces it), so the
        // payload must omit direction + canonical.
        const sfWf = subflowDetailQueries.find((q) => q.data?.id === sfId)?.data;
        const sfName = sfWf?.name ?? `sub-flow ${sfId.slice(0, 8)}`;
        const sfDef: WorkflowDefinition = {
          name: sfName,
          kind: "orchestration",
          direction: null,
          canonical: null,
          description: sfWf?.description ?? null,
          nodes: slice.nodes,
          edges: slice.edges,
        };
        await workflowsApi.createVersion(sfId, {
          definition: sfDef,
          notes: `Edited inline from parent ${wfQuery.data.name}`,
          activate,
        });
      }

      // Main save — same filter as buildLiveDefinition / the dirty
      // memo so any sub-flow leftover (region wrapper, prefixed child,
      // virtual link edge) is stripped before serialisation.
      if (!shouldSaveMain(isMainModified, dirtySubflowIds.length)) {
        // Sub-flows changed but the main canvas didn't — skip the
        // main createVersion to avoid creating an empty no-op version
        // on the parent. (shouldSaveMain centralises this rule.)
        return null;
      }
      const mainNodes = nodes.filter(
        (n) => n.type !== "subflowRegion" && n.parentNode === undefined && !isSubflowChildNode(n),
      );
      const mainEdges = edges.filter(
        (e) =>
          !isSubflowChildNode({ id: e.source }) &&
          !isSubflowChildNode({ id: e.target }) &&
          !isSubflowChildNode(e) &&
          !isSubflowLinkEdge(e),
      );
      const { nodes: trayNodes, edges: trayEdges } = flowToTray(mainNodes, mainEdges);
      // Pydantic validator on WorkflowDefinition is strict per kind
      // (canonical needs direction+canonical; orchestration forbids both;
      // app requires app_id, forbids both) — definitionKindFields emits the
      // matching shape so the version-create guard doesn't reject a kind
      // mismatch. See the helper's docstring.
      const definition: WorkflowDefinition = {
        name: wfQuery.data.name,
        ...definitionKindFields(wfQuery.data),
        description: wfQuery.data.description ?? null,
        error_workflow_id: errorWorkflowId,
        nodes: trayNodes,
        edges: trayEdges,
      };
      return workflowsApi.createVersion(workflowId, {
        definition,
        notes: null,
        activate,
      });
    },
    onSuccess: () => {
      setSaveError(null);
      // After save, snap back to following the active version (which is
      // either the brand-new one we just created+activated, or the same
      // as before if we saved as draft). Either way, "follow active"
      // is the right default after a save.
      setLoadedVersionId(null);
      // Saved state IS the new baseline — undoing into PRE-save edits
      // would re-introduce diffs against a now-shipped version. Cleanest
      // to start fresh.
      clearHistory();
      qc.invalidateQueries({ queryKey: ["workflow", workflowId] });
      qc.invalidateQueries({ queryKey: ["workflows"] });
      // ADR-0008 PR3b3c — invalidate every sub-flow we just patched
      // so useQueries refetches and the hydration effect re-snapshots
      // them (flipping dirtySubflowIds back to empty).
      for (const sfId of dirtySubflowIds) {
        qc.invalidateQueries({ queryKey: ["workflow", sfId] });
      }
    },
    onError: (err: Error) => setSaveError(err.message),
  });

  // ADR-0008 PR3b3 — actual implementation of the sub-flow create
  // handler (the ref is declared earlier so the nodesWithLeaf memo
  // can wire it onto every sink node before this lands).
  //
  // 1. POST /workflows/{id}/sub-flow-for-branch — backend allocates a
  //    hidden orchestration workflow with a delivery_trigger source
  //    + activates v1
  // 2. Patch the parent's sink config so branches[branch_id] carries
  //    the new linked_workflow_id (adds the entry for implicit
  //    branches like succeeded / dead_letter if missing)
  // 3. Save the parent — activate so the link-validator + drain's
  //    sub-flow snapshot pick up the new link immediately.
  //
  // We build the patched definition LOCALLY (not via saveMutation
  // which reads from React state) because React state updates are
  // async — the setNodes patch wouldn't be visible to a saveMutation
  // that fires right after. Bug pattern that previously made the `+`
  // button flicker without effect.
  React.useEffect(() => {
    createBranchSubflowRef.current = async (nodeId: string, branchId: string) => {
      // Single in-flight guard, read from current React state.
      setCreatingSubflowFor((curr) => {
        if (curr) return curr;
        return { nodeId, branchId };
      });
      try {
        const wfData = wfQuery.data;
        if (!wfData) throw new Error("workflow not loaded");

        // 1. Allocate the sub-flow workflow on the backend.
        const subflow = await workflowsApi.createSubflowForBranch(workflowId, {
          sink_node_id: nodeId,
          branch_id: branchId,
        });

        // 2. Build the patched canvas nodes LOCALLY (don't rely on
        //    the async setNodes propagation). We need them both to
        //    update React state AND to build the definition we'll
        //    POST in step 3.
        const patchedNodes = nodes.map((n) => {
          if (n.id !== nodeId) return n;
          const cfg = (n.data.config ?? {}) as Record<string, unknown>;
          const branches = (cfg.branches as Array<Record<string, unknown>>) ?? [];
          const idx = branches.findIndex((b) => b?.id === branchId);
          let nextBranches: Array<Record<string, unknown>>;
          if (idx >= 0) {
            nextBranches = branches.map((b, i) =>
              i === idx ? { ...b, linked_workflow_id: subflow.id } : b,
            );
          } else {
            nextBranches = [
              ...branches,
              {
                id: branchId,
                label: branchId.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
                trigger: "auto",
                linked_workflow_id: subflow.id,
              },
            ];
          }
          return {
            ...n,
            data: {
              ...n.data,
              config: { ...cfg, branches: nextBranches },
            },
          };
        });
        setNodes(patchedNodes);

        // 3. Build the workflow definition from the PATCHED nodes
        //    (not from React state — that hasn't propagated yet) and
        //    POST a new active version directly. Filter sub-flow
        //    region/children so they don't pollute the main def.
        const mainNodes = patchedNodes.filter(
          (n) => n.type !== "subflowRegion" && n.parentNode === undefined && !isSubflowChildNode(n),
        );
        const mainEdges = edges.filter(
          (e) =>
            !isSubflowChildNode({ id: e.source }) &&
            !isSubflowChildNode({ id: e.target }) &&
            !isSubflowChildNode(e),
        );
        const { nodes: trayNodes, edges: trayEdges } = flowToTray(mainNodes, mainEdges);
        // Emit the workflow's OWN kind fields (canonical/orchestration/app)
        // via the shared helper — a hand-rolled binary ternary defaulted
        // app-kind workflows to "orchestration" (backend 422) — AND carry
        // error_workflow_id so persisting this parent version doesn't wipe
        // the configured on-failure handler.
        const definition: WorkflowDefinition = {
          name: wfData.name,
          ...definitionKindFields(wfData),
          description: wfData.description ?? null,
          error_workflow_id: errorWorkflowId,
          nodes: trayNodes,
          edges: trayEdges,
        };
        await workflowsApi.createVersion(workflowId, {
          definition,
          notes: `Auto-save: linked sub-flow on ${nodeId}.${branchId}`,
          activate: true,
        });

        // 4. Invalidate the parent + workflows list + the newly-created
        //    sub-flow detail so useQueries can render the region inline.
        qc.invalidateQueries({ queryKey: ["workflow", workflowId] });
        qc.invalidateQueries({ queryKey: ["workflows"] });
        qc.invalidateQueries({ queryKey: ["workflow", subflow.id] });
      } catch (err) {
        setSaveError((err as Error).message);
        // eslint-disable-next-line no-console
        console.error("createBranchSubflow failed", err);
      } finally {
        setCreatingSubflowFor(null);
      }
    };
  }, [workflowId, wfQuery.data, nodes, edges, setNodes, qc]);

  // ADR-0008 PR3b3b — sub-flow delete handler. Wired into the trash
  // icon on every SubflowRegionNode.
  //
  // 1. Patch the parent node's config.branches: drop the entry whose
  //    linked_workflow_id matches the sub-flow being deleted. (If
  //    multiple branches happen to link the same sub-flow we keep the
  //    others — only the matching entry's linked_workflow_id is set
  //    to null so the branch survives as an unlinked hook point.)
  // 2. Save the parent (activate). Same stale-closure-safe pattern
  //    as createBranchSubflow — we build the patched definition
  //    locally instead of relying on setNodes propagation.
  // 3. DELETE the sub-flow workflow on the backend. CASCADE on
  //    parent_workflow_id would catch it on a parent delete, but
  //    we want explicit cleanup here (the parent isn't going away).
  // 4. Invalidate queries so the canvas re-renders without the
  //    region.
  React.useEffect(() => {
    deleteSubflowRef.current = async (subflowId: string) => {
      try {
        const wfData = wfQuery.data;
        if (!wfData) throw new Error("workflow not loaded");

        // Locally compute the patched nodes (clear linked_workflow_id
        // on every branch that points at this sub-flow). We don't
        // remove the branch entry itself — implicit branches must
        // stay implicit-only after unlinking, and operator-declared
        // custom branches keep their label/trigger config.
        const patchedNodes = nodes.map((n) => {
          if (n.type === "subflowRegion") return n;
          if (n.parentNode) return n;
          const cfg = (n.data?.config ?? {}) as Record<string, unknown>;
          const branches = (cfg.branches as Array<Record<string, unknown>>) ?? [];
          let touched = false;
          const nextBranches = branches.map((b) => {
            if (b?.linked_workflow_id === subflowId) {
              touched = true;
              return { ...b, linked_workflow_id: null };
            }
            return b;
          });
          if (!touched) return n;
          return {
            ...n,
            data: {
              ...n.data,
              config: { ...cfg, branches: nextBranches },
            },
          };
        });
        setNodes(patchedNodes);

        // Save the parent with the unlinked branches.
        const mainNodes = patchedNodes.filter(
          (n) => n.type !== "subflowRegion" && n.parentNode === undefined && !isSubflowChildNode(n),
        );
        const mainEdges = edges.filter(
          (e) =>
            !isSubflowChildNode({ id: e.source }) &&
            !isSubflowChildNode({ id: e.target }) &&
            !isSubflowChildNode(e) &&
            !isSubflowLinkEdge(e),
        );
        const { nodes: trayNodes, edges: trayEdges } = flowToTray(mainNodes, mainEdges);
        // Emit the workflow's OWN kind fields (canonical/orchestration/app)
        // via the shared helper — a hand-rolled binary ternary defaulted
        // app-kind workflows to "orchestration" (backend 422) — AND carry
        // error_workflow_id so persisting this parent version doesn't wipe
        // the configured on-failure handler.
        const definition: WorkflowDefinition = {
          name: wfData.name,
          ...definitionKindFields(wfData),
          description: wfData.description ?? null,
          error_workflow_id: errorWorkflowId,
          nodes: trayNodes,
          edges: trayEdges,
        };
        await workflowsApi.createVersion(workflowId, {
          definition,
          notes: `Auto-save: removed sub-flow ${subflowId.slice(0, 8)}…`,
          activate: true,
        });

        // Now drop the sub-flow workflow row. CASCADE on
        // parent_workflow_id would catch it on a parent delete, but
        // the parent is still around — explicit DELETE is the only
        // way to clean up the hidden row.
        try {
          await workflowsApi.delete(subflowId);
        } catch (err) {
          // The parent's save already unlinked the branch — even if
          // the sub-flow delete fails (FK in use somewhere, etc.) the
          // operator's intent is satisfied. Just log; don't fail the
          // whole op.
          // eslint-disable-next-line no-console
          console.warn("subflow workflow delete failed", err);
        }

        qc.invalidateQueries({ queryKey: ["workflow", workflowId] });
        qc.invalidateQueries({ queryKey: ["workflows"] });
        qc.removeQueries({ queryKey: ["workflow", subflowId] });
      } catch (err) {
        setSaveError((err as Error).message);
        // eslint-disable-next-line no-console
        console.error("deleteSubflow failed", err);
      }
    };
  }, [workflowId, wfQuery.data, nodes, edges, setNodes, qc]);

  // Activate an existing (already-saved) version — 1-click rollback or
  // promote-draft. Distinct from save: no definition changes, just flips
  // the active_version_id pointer on the workflow row.
  const activateMutation = useMutation({
    mutationFn: async (versionId: string) => workflowsApi.activateVersion(workflowId, versionId),
    onSuccess: () => {
      setSaveError(null);
      setLoadedVersionId(null);
      qc.invalidateQueries({ queryKey: ["workflow", workflowId] });
      qc.invalidateQueries({ queryKey: ["workflows"] });
    },
    onError: (err: Error) => setSaveError(err.message),
  });

  // Auto-layout (operator-triggered, never silent): reorder the MAIN nodes
  // into depth columns via the pure `autoLayoutPositions`; sub-flow regions +
  // their children keep their own positions (regions persist theirs on the
  // parent sink's branch config). Marks the editor dirty — the operator saves.
  const applyAutoLayout = React.useCallback(() => {
    setNodes((nds) => {
      const main = nds.filter(
        (n) => n.type !== "subflowRegion" && n.parentNode === undefined && !isSubflowChildNode(n),
      );
      const loopIds = new Set(
        main.filter((n) => (n.data as NodeData | undefined)?.kind === "loop").map((n) => n.id),
      );
      const pos = autoLayoutPositions(
        main.map((n) => n.id),
        edges.map((e) => ({ source: e.source, target: e.target })),
        loopIds,
      );
      return nds.map((n) => {
        const p = pos.get(n.id);
        return p ? { ...n, position: p } : n;
      });
    });
  }, [edges, setNodes]);

  // Discard a saved version (draft-first cleanup: stacked Odino/operator
  // drafts had no way out). The API refuses the ACTIVE version and any
  // version with runs, so this only ever removes an inert draft. If the
  // deleted version was the one LOADED, fall back to following the active.
  const deleteVersionMutation = useMutation({
    mutationFn: async (versionId: string) => workflowsApi.deleteVersion(workflowId, versionId),
    onSuccess: (_res, versionId) => {
      setSaveError(null);
      if (loadedVersionId === versionId) setLoadedVersionId(null);
      qc.invalidateQueries({ queryKey: ["workflow", workflowId] });
      qc.invalidateQueries({ queryKey: ["workflows"] });
    },
    onError: (err: Error) => setSaveError(err.message),
  });

  // Continue from a pinned run — fires DIRECTLY without opening the
  // TestRunDrawer (the old behaviour confused users: re-opening the
  // payload form on Continue made it look like a normal "Test run"
  // when really the input is replayed server-side from the parent
  // execution). Sends resume_from_execution_id; the backend skips
  // cached upstream nodes (handler bypass + cached output dispatch)
  // and only re-runs anything downstream of the pin. The cached rows
  // come back tagged with ``_pinned_from`` in log_outputs.
  const continueRunMutation = useMutation({
    mutationFn: async () => {
      if (!pinnedExecutionId) throw new Error("no pinned execution");
      // Safe to deref: the Continue button only renders after a
      // successful pinned run, which means wfQuery has already resolved.
      const wfData = wfQuery.data!;
      const { nodes: trayNodes, edges: trayEdges } = flowToTray(nodes, edges);
      // Emit the workflow's OWN kind fields via the shared helper (a
      // hand-rolled ternary defaulted app-kind to "orchestration" → 422) +
      // carry error_workflow_id so a continue-run version doesn't drop the
      // on-failure handler.
      const definition: WorkflowDefinition = {
        name: wfData.name,
        ...definitionKindFields(wfData),
        description: wfData.description ?? null,
        error_workflow_id: errorWorkflowId,
        nodes: trayNodes,
        edges: trayEdges,
      };
      // Arm the SSE listener so the editor latches the new execution
      // id as soon as EXECUTION_STARTED arrives — gives the canvas
      // live per-node updates exactly as a normal Test run.
      armedForLiveRef.current = true;
      return workflowsApi.testRun(workflowId, {
        payload: null,
        definition,
        resume_from_execution_id: pinnedExecutionId,
      });
    },
    onSuccess: (resp) => {
      // The synchronous response IS the completion signal — exit live mode
      // so the traversed edges stop animating (marching-ants over a panned
      // canvas is the source of the whole-app paint flicker). Same reasoning
      // as onRunComplete.
      armedForLiveRef.current = false;
      setLiveExecutionId(null);
      setEditorTestRun(resp);
      setLogsOpen(true);
      // Keep the pin alive — the user might want to Continue again
      // (e.g. after another config tweak). Trace overlay reflects the
      // newest run, with cached rows marked via _pinned_from.
    },
  });

  // Discard local edits — force a FULL re-hydration of the currently
  // loaded version. We bump ``hydrationNonce`` instead of rebuilding the
  // canvas by hand: the hydration effect is the single source of truth
  // that rebuilds the main flow AND re-splats every sub-flow region (+
  // re-snapshots + resets history). The previous hand-rolled rebuild
  // only re-created the MAIN nodes/edges, so discarding made every
  // sub-flow region vanish until a page reload.
  const discardChanges = React.useCallback(() => {
    if (!wfQuery.data) return;
    setHydrationNonce((n) => n + 1);
  }, [wfQuery.data]);

  // ─── Run-history overlay ──────────────────────────────────────────
  // The recent-runs list now lives in ExecutionsListSidebar (only
  // mounted in Executions mode). Here we only need the *detail* of
  // whichever run the user picked, for the canvas overlay + drawer.
  //
  // When the user picks a run, fetch its detail (with per-node statuses
  // + input/output payloads). Drives the canvas overlay + the drawer's
  // read-only execution view.
  const viewedRunQ = useQuery({
    queryKey: ["workflow", workflowId, "run-detail", viewedRunId],
    queryFn: () => workflowExecutionsApi.get(viewedRunId!),
    enabled: viewedRunId !== null,
    staleTime: 60_000,
  });

  // Per-node status map for the canvas overlay. Driven by either the
  // viewed run (if any) OR the last test-run; viewed run wins because
  // the user explicitly asked to see it.
  React.useEffect(() => {
    if (viewedRunId === null) return;
    const det = viewedRunQ.data;
    if (!det) return;
    const map: Record<string, string> = {};
    for (const n of det.nodes) {
      // Keep the worst status per node when iterations differ (e.g. one
      // iter failed → mark the whole node failed).
      const prev = map[n.node_id];
      if (
        !prev ||
        (n.status === "failed" && prev !== "failed") ||
        (prev === "succeeded" && n.status === "skipped")
      ) {
        map[n.node_id] = n.status;
      }
    }
    setTraceStatus(map);
  }, [viewedRunId, viewedRunQ.data]);

  // Drop the overlay when the viewed run is cleared.
  React.useEffect(() => {
    if (viewedRunId === null) {
      setTraceStatus({});
      setNodeProgress({});
      setNodeLiveCount({});
      liveCountRef.current = newLiveCountState();
      setLogsOpen(false);
    }
  }, [viewedRunId]);

  // Default the logs footer to OPEN whenever a run (past or just-run)
  // is loaded — most of the value of having a run is seeing the trace,
  // so showing it by default beats forcing every user to click the
  // chevron first.
  React.useEffect(() => {
    if (viewedRunQ.data || editorTestRun) {
      setLogsOpen(true);
    }
  }, [viewedRunQ.data, editorTestRun]);

  // ─── Live (SSE) per-node overlay ───────────────────────────────────
  // Grab the rolling event buffer + a generation counter that bumps on
  // every new event. We consume it via two useEffects:
  //   1. When "armed" (user just clicked Test run), watch for the
  //      EXECUTION_STARTED on this workflow and capture its
  //      execution_id → that puts us into "live" mode.
  //   2. While in live mode, fold node_started/finished events into
  //      the traceStatus map so the canvas ring transitions in real
  //      time. EXECUTION_FINISHED clears the live id and we go back
  //      to passive (the synchronous test-run response handles the
  //      final summary via the existing onRunComplete callback).
  const liveEvents = useLiveEvents();

  React.useEffect(() => {
    if (!armedForLiveRef.current) return;
    for (const ev of liveEvents.events) {
      if (
        ev.kind === "execution_started" &&
        (ev as { workflow_id?: string }).workflow_id === workflowId
      ) {
        armedForLiveRef.current = false;
        const eid = String((ev as { execution_id?: string }).execution_id ?? "");
        if (eid) {
          setLiveExecutionId(eid);
          setTraceStatus({}); // wipe stale overlay from a previous run
          setNodeProgress({});
          setNodeLiveCount({});
          liveCountRef.current = newLiveCountState();
        }
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveEvents.generation, workflowId]);

  React.useEffect(() => {
    if (!liveExecutionId) return;
    let changed = false;
    let finished = false;
    let progressChanged = false;
    const next: Record<string, string> = { ...traceStatus };
    const nextProgress: Record<string, { done: number; total: number }> = {
      ...nodeProgress,
    };
    // The SSE buffer is NEWEST-FIRST. A LOOP body node runs once PER BATCH
    // under the same execution_id, so it emits started→finished repeatedly.
    // To show each node's CURRENT status we must keep ONLY its newest
    // started/finished event and ignore the older ones — otherwise an older
    // batch's event bleeds over the live one and every body node lights up
    // "running" (or flickers) at once. `seenStatus` = node already took its
    // newest status this pass.
    const seenStatus = new Set<string>();
    // Live per-node output count, summed across the node_finished events of this
    // execution (a loop body emits one per batch → sum = total). ACCUMULATED
    // into a ref + deduped by event id — see the `liveCountRef` note: the buffer
    // is a capped ring, so re-summing it would lose evicted events.
    const countChanged = foldLiveCounts(
      liveEvents.events as unknown as CountableEvent[],
      liveCountRef.current,
      { executionId: liveExecutionId, keyOf: (e) => e.node_id ?? null },
    );
    for (const ev of liveEvents.events) {
      const e = ev as {
        execution_id?: string;
        node_id?: string;
        status?: string;
        done?: number;
        total?: number;
        items_out?: number;
      };
      if (e.execution_id !== liveExecutionId) continue;
      if (ev.kind === "execution_finished") {
        finished = true;
        continue;
      }
      // ADR-0067 S2: throttled batch progress overlaid on a running node.
      // The SSE buffer is NEWEST-FIRST, so we must NOT last-write-wins (that
      // lands on the OLDEST buffered tick → the bar froze at the first ~500).
      // Take the MAX `done` (progress is monotonic) so ordering can't matter,
      // and never (re)show progress for a node that has already finished — its
      // `node_finished` is newer in the buffer, so it's seen BEFORE these older
      // ticks and already marked `next[node]` terminal; without this guard the
      // older ticks resurrect the deleted bar.
      if (ev.kind === "node_progress") {
        if (!e.node_id || e.done == null || e.total == null) continue;
        const st = next[e.node_id];
        if (st && st !== "running") {
          // Finished (or terminal from a prior pass) → drop any lingering bar.
          if (nextProgress[e.node_id]) {
            delete nextProgress[e.node_id];
            progressChanged = true;
          }
          continue;
        }
        const cur = nextProgress[e.node_id];
        if (!cur || e.done > cur.done || e.total !== cur.total) {
          nextProgress[e.node_id] = { done: e.done, total: e.total };
          progressChanged = true;
        }
        continue;
      }
      if (ev.kind !== "node_started" && ev.kind !== "node_finished") continue;
      if (!e.node_id || !e.status) continue;
      // (the live output count is accumulated above, via foldLiveCounts)
      // Newest-first: the FIRST event we see for a node IS its latest status;
      // skip every older one so a prior batch can't override the live state.
      if (seenStatus.has(e.node_id)) continue;
      seenStatus.add(e.node_id);
      // A node that just finished no longer shows batch progress.
      if (ev.kind === "node_finished" && nextProgress[e.node_id]) {
        delete nextProgress[e.node_id];
        progressChanged = true;
      }
      if (next[e.node_id] !== e.status) {
        next[e.node_id] = e.status;
        changed = true;
      }
    }
    if (changed) setTraceStatus(next);
    if (progressChanged) setNodeProgress(nextProgress);
    // Publish the live counts only when they actually changed (avoid churn).
    if (countChanged) setNodeLiveCount({ ...liveCountRef.current.counts });
    if (finished) setLiveExecutionId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveEvents.generation, liveExecutionId]);

  /** True while the canvas is in "view past run" mode — editing actions
   *  (drag, connect, delete) are blocked to avoid creating a confusing
   *  mismatch between the historical run's definition and the user's
   *  in-progress edits. The view mode is the master switch: once we
   *  flip to "executions", the canvas is read-only even before a
   *  specific run is selected (so accidental edits are impossible). */
  const isViewingPastRun = viewMode === "executions";

  /** ExecutionDetail-shaped value the bottom RunDetailsPanel renders.
   *  Two sources, in priority order:
   *    1. The picked past run (Executions mode).
   *    2. The latest in-editor test-run, synthesised to the same shape
   *       so the panel doesn't need two code paths.
   *  ``null`` means there's nothing to show — panel renders nothing. */
  const activeRunDetail: ExecutionDetail | null = React.useMemo(() => {
    if (viewedRunQ.data) return viewedRunQ.data;
    if (!editorTestRun) return null;
    // Synthesise an ExecutionDetail-ish object from a TestRunResponse.
    // The id / timestamps are placeholders ("test-run" + now) because
    // test runs aren't persisted as workflow_executions rows.
    const now = new Date().toISOString();
    return {
      id: "test-run",
      workflow_id: workflowId,
      workflow_name: wfQuery.data?.name ?? "",
      workflow_version_id: wfQuery.data?.active_version_id ?? "",
      workflow_version: null,
      direction: wfQuery.data?.direction ?? "inbound",
      message_id: null,
      trigger: "replay",
      status: editorTestRun.status,
      started_at: now,
      finished_at: now,
      error: editorTestRun.error,
      node_count: editorTestRun.node_traces.length,
      failed_node_count: editorTestRun.node_traces.filter((t) => t.status === "failed").length,
      delivery_summary: {
        pending: 0,
        in_flight: 0,
        succeeded: 0,
        failed: 0,
        dead_letter: 0,
      },
      parent_execution_id: null,
      nodes: editorTestRun.node_traces.map((t, i) => ({
        id: `test-${i}`,
        node_id: t.node_id,
        node_kind: t.node_kind,
        status: t.status,
        iteration: t.iteration,
        duration_ms: t.duration_ms,
        error: t.error,
        started_at: now,
        finished_at: now,
        input: t.input,
        output: t.output,
        log_outputs: t.log_outputs,
      })),
      children: [],
    };
  }, [viewedRunQ.data, editorTestRun, workflowId, wfQuery.data]);

  // n8n-style per-edge item count. From the active run's trace we sum each
  // node's OUTPUT batch length across all its executions (a loop body runs
  // once per batch, so summing gives the "total items" that left the node).
  // Each outgoing edge is then labelled with its SOURCE node's total — the
  // number of items that flowed down that connection.
  const nodeOutCount = React.useMemo<Record<string, number>>(() => {
    if (!activeRunDetail) return {};
    const batchLen = (out: unknown): number | null => {
      if (Array.isArray(out)) return out.length;
      if (out && typeof out === "object" && Array.isArray((out as { items?: unknown[] }).items))
        return (out as { items: unknown[] }).items.length;
      return null;
    };
    const acc: Record<string, number> = {};
    for (const n of activeRunDetail.nodes) {
      if (n.status !== "succeeded") continue;
      const c = batchLen(n.output);
      if (c == null) continue;
      acc[n.node_id] = (acc[n.node_id] ?? 0) + c;
    }
    return acc;
  }, [activeRunDetail]);

  // Per-node item count that drives the edge pills: the LIVE counts (from
  // node_finished events, appearing as each node finishes) overlaid by the
  // FINAL trace counts once the run completes (authoritative, exact).
  const edgeItemCount = React.useMemo<Record<string, number>>(
    () => ({ ...nodeLiveCount, ...nodeOutCount }),
    [nodeLiveCount, nodeOutCount],
  );

  // Overlay the item-count onto each edge (live during a run, final after).
  const edgesForCanvas = React.useMemo<Edge[]>(() => {
    if (Object.keys(edgeItemCount).length === 0) return edgesWithActions;
    return edgesWithActions.map((e) => {
      const count = edgeItemCount[e.source];
      if (count === undefined) return e;
      return {
        ...e,
        data: { ...((e.data ?? {}) as object), itemCount: count } as TrayEdgeActionsData,
      };
    });
  }, [edgesWithActions, edgeItemCount]);

  // Keyboard shortcuts at the canvas wrapper level. Skipped when we're
  // typing in any input/textarea (so the user can backspace text
  // without nuking the selected node) or in past-run mode (read-only).
  React.useEffect(() => {
    function inEditable(target: EventTarget | null): boolean {
      const el = target as HTMLElement | null;
      if (!el) return false;
      const tag = el.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
    }
    function onKey(e: KeyboardEvent) {
      if (inEditable(e.target)) return;
      if (isViewingPastRun) return;

      const mod = e.metaKey || e.ctrlKey;
      const key = e.key.toLowerCase();

      // Cmd/Ctrl + Z (without Shift) → undo
      // Cmd/Ctrl + Shift + Z, Cmd/Ctrl + Y → redo
      // Browsers vary on which combo is "redo"; we accept both so muscle
      // memory works whether you came from VS Code, IntelliJ, or Figma.
      if (mod && key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      if (mod && ((key === "z" && e.shiftKey) || key === "y")) {
        e.preventDefault();
        redo();
        return;
      }

      // Cmd/Ctrl + D → duplicate selected node
      if (mod && key === "d") {
        if (selectedId) {
          e.preventDefault();
          duplicateNode(selectedId);
        }
      }
      // Delete / Backspace → remove selected node (+ its edges)
      if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
        e.preventDefault();
        deleteNode(selectedId);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isViewingPastRun, selectedId, duplicateNode, undo, redo]);

  // When traceStatus changes (after a test-run), merge the per-node
  // status into each ReactFlow node's data so TrayNodeBox can render
  // the success/failure ring without us touching the data shape on
  // every render of unrelated state.
  React.useEffect(() => {
    if (Object.keys(traceStatus).length === 0) {
      // Clear any previous status overlay.
      setNodes((nds) =>
        nds.map((n) =>
          n.data.traceStatus === undefined && n.data.progress === undefined
            ? n
            : { ...n, data: { ...n.data, traceStatus: undefined, progress: undefined } },
        ),
      );
      // Also drop the "traversed" green tint off every edge.
      setEdges((eds) =>
        eds.map((e) => (e.animated || e.style ? { ...e, animated: false, style: undefined } : e)),
      );
      return;
    }
    setNodes((nds) =>
      nds.map((n) => ({
        ...n,
        data: { ...n.data, traceStatus: traceStatus[n.id], progress: nodeProgress[n.id] },
      })),
    );
    // Paint each edge that the run actually traversed green: the source node
    // finished AND the target was reached (running/succeeded/failed). A
    // not-taken IF/loop branch (target skipped or never touched) stays neutral
    // so the green path traces the REAL flow through the graph.
    setEdges((eds) =>
      eds.map((e) => {
        const src = traceStatus[e.source];
        const tgt = traceStatus[e.target];
        const traversed =
          src === "succeeded" && (tgt === "succeeded" || tgt === "running" || tgt === "failed");
        if (!traversed) {
          return e.animated || e.style ? { ...e, animated: false, style: undefined } : e;
        }
        // Animate the dashes ONLY while the run is in flight; once it
        // finishes the edge stays green but STATIC (a frozen flow, not a
        // perpetual marching-ants).
        return {
          ...e,
          animated: liveExecutionId !== null,
          style: { stroke: "#10b981", strokeWidth: 2.5 },
        };
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traceStatus, nodeProgress, liveExecutionId]);

  // ADR-0012 — per-node output for the drawer's INPUT "N nodes back"
  // picker. Prefer the viewed past run; else the live editor test-run.
  // MUST stay ABOVE the loading/error early-returns below — otherwise
  // it's a conditional hook ("Rendered more hooks than during the
  // previous render" when the query resolves between renders).
  const drawerNodeOutputs = React.useMemo<Record<string, unknown>>(() => {
    const out: Record<string, unknown> = {};
    if (isViewingPastRun && viewedRunQ.data?.nodes) {
      for (const n of viewedRunQ.data.nodes) out[n.node_id] = n.output;
    } else if (editorTestRun?.node_traces) {
      for (const t of editorTestRun.node_traces) out[t.node_id] = t.output;
    }
    return out;
  }, [isViewingPastRun, viewedRunQ.data, editorTestRun]);

  // The twin of `drawerNodeOutputs`, carrying each node's `log_outputs`. The
  // Input pane needs it to page an UPSTREAM node's loop iterations: the run
  // WINDOWS ride on `log_outputs.runs` (see backend `hub.workflow.node_runs`),
  // and passing only the payload meant the run selector could only ever appear
  // on this node's own input — the owner reported exactly that.
  const drawerNodeLogOutputs = React.useMemo<Record<string, Record<string, unknown>>>(() => {
    const out: Record<string, Record<string, unknown>> = {};
    if (isViewingPastRun && viewedRunQ.data?.nodes) {
      for (const n of viewedRunQ.data.nodes) out[n.node_id] = n.log_outputs ?? {};
    } else if (editorTestRun?.node_traces) {
      for (const t of editorTestRun.node_traces) out[t.node_id] = t.log_outputs ?? {};
    }
    return out;
  }, [isViewingPastRun, viewedRunQ.data, editorTestRun]);

  // Full per-node trace (input + output + status + logs) from the LIVE editor
  // test-run, keyed by node id. The drawer seeds its INPUT/OUTPUT panes from
  // this on open so a node re-opened after a test-run still shows what flowed
  // through it — instead of going blank because the transient "Run node"
  // result was cleared on node-change. Past-run view uses
  // ``viewedRunNodeExecutions`` instead, so this is live-run only.
  const drawerNodeTraces = React.useMemo<Record<string, TestRunNodeTrace>>(() => {
    const out: Record<string, TestRunNodeTrace> = {};
    if (!isViewingPastRun && editorTestRun?.node_traces) {
      // Last trace wins for fan-out nodes (SPLIT/LOOP) — the final iteration
      // is the most representative single snapshot for the pane.
      for (const t of editorTestRun.node_traces) out[t.node_id] = t;
    }
    return out;
  }, [isViewingPastRun, editorTestRun]);

  if (wfQuery.isLoading || catalogQuery.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (wfQuery.isError) {
    return (
      <Alert tone="danger" className="m-6">
        {(wfQuery.error as Error)?.message ?? "Failed to load workflow"}
      </Alert>
    );
  }
  const wf = wfQuery.data!;
  // Direction is canonical-only — for orchestration workflows it's null
  // and the palette ignores it. We fall back to 'inbound' as a no-op
  // default for fields that still expect a non-null value (legacy
  // call-sites that haven't been kind-aware-ed yet).
  const direction: Direction = wf.direction ?? "inbound";
  // ``selectedId`` powers the visual ring on the canvas + the keyboard
  // shortcuts (Cmd+D / Delete). ``drawerNodeId`` powers the drawer.
  // Click → selects; double-click → opens drawer. The two intentionally
  // diverge: you can have a node selected (highlighted, hotkey target)
  // without the drawer being open.
  const drawerFlowNode = nodes.find((n) => n.id === drawerNodeId) ?? null;
  const drawerTrayNode: TrayNode | null = drawerFlowNode
    ? {
        id: drawerFlowNode.id,
        kind: drawerFlowNode.data.kind,
        label: drawerFlowNode.data.label,
        config: drawerFlowNode.data.config,
        execute_once: drawerFlowNode.data.execute_once ?? false,
        notes: drawerFlowNode.data.notes ?? null,
        always_output_data: drawerFlowNode.data.always_output_data ?? false,
        retry_on_fail: drawerFlowNode.data.retry_on_fail ?? false,
        max_tries: drawerFlowNode.data.max_tries ?? 3,
        retry_wait_ms: drawerFlowNode.data.retry_wait_ms ?? 1000,
        on_error: drawerFlowNode.data.on_error ?? "stop",
        execution_mode: drawerFlowNode.data.execution_mode ?? null,
        position: { x: drawerFlowNode.position.x, y: drawerFlowNode.position.y },
      }
    : null;
  const drawerSpec = drawerTrayNode ? catalogIdx[drawerTrayNode.kind] : null;

  // Back-to-list URL depends on kind so the user lands on the right
  // list page (and the sidebar highlights the right entry).
  const listUrl = "/workflows";
  const listLabel = wf.kind === "orchestration" ? "All automations" : "All workflows";

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* fill the layout's
      bounded <main> (flex-1 overflow-hidden) exactly — a viewport calc
      overshot the real container height (topbar + margin), pushing the
      bottom logs dock below the overflow-hidden clip. */}
      {/* Top bar — ONE row only.

          Rules of thumb:
            * Identity (back · name · status dot) on the left
            * Primary actions (Test run · Save) on the right
            * Everything else lives behind hover (tooltip on the name
              shows description; status dot tooltip shows the version
              context) or behind the ⋮ menu (version switcher, edit
              metadata, enable/disable, delete).

          The earlier 3-row layout (kind chip + description + version
          row) was too dense to skim — the user said "non si capisce
          una mazza". Strip it to the essentials. */}
      <div className="flex items-center justify-between gap-3 border-b border-border bg-surface px-3 py-2">
        <div className="flex min-w-0 items-center gap-3">
          <Link
            to={listUrl}
            className="inline-flex shrink-0 items-center text-muted-foreground hover:text-accent"
            title={listLabel}
            aria-label={listLabel}
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
          </Link>

          {/* Title — the description rides on the tooltip so it stays
              available without burning a second row. */}
          <div
            className="truncate text-sm font-semibold"
            title={wf.description ? `${wf.name} — ${wf.description}` : wf.name}
          >
            {wf.name}
          </div>

          {/* Inline edit pencil — admin-only, same target as the ⋮
              menu's "Edit metadata" entry; surfaced here so it's
              discoverable on hover of the title block. */}
          {canWrite && (
            <button
              type="button"
              onClick={() => setEditMetadataOpen(true)}
              className="rounded p-0.5 text-muted-foreground/40 transition hover:bg-muted hover:text-foreground"
              title="Edit name, description, tags"
              aria-label="Edit workflow metadata"
            >
              <Pencil className="h-3 w-3" strokeWidth={1.5} />
            </button>
          )}

          {/* Status chips — one per orthogonal dimension so a glance
              decodes without a legend:

                1. Version pill (always shown): vN · active|legacy
                   ─ green tint when you're on the active version
                   ─ muted tint when viewing an older one (with a hint
                     of which version IS active, so rollback is obvious)
                2. "● unsaved" (only when dirty) — editor state, totally
                   independent of enabled/disabled and version
                3. "disabled" (only when wf.enabled === false) — runtime
                   gate; says nothing about the canvas state

              Mixing all three into one stoplight dot (previous design)
              made the disabled-vs-modified-vs-rollback story
              unreadable. Three separate chips, each only shown when
              meaningful, scans much faster. */}
          {viewedVersion && (
            <span
              className={
                "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[11px] " +
                (isViewingActive ? "bg-success/15 text-success" : "bg-muted text-muted-foreground")
              }
              title={
                isViewingActive
                  ? `Loaded v${viewedVersion.version} — this is the active version`
                  : `Loaded v${viewedVersion.version} — ACTIVE is v${wf.active_version_number ?? "?"}. Save to fork from here, or activate this version from the ⋮ menu.`
              }
            >
              v{viewedVersion.version}
              <span className="opacity-60">·</span>
              <span className="text-[10px] uppercase tracking-wide">
                {isViewingActive ? "active" : `active: v${wf.active_version_number ?? "?"}`}
              </span>
            </span>
          )}

          {isModified && (
            <span
              className="inline-flex shrink-0 items-center gap-1 rounded-full bg-warning/15 px-2 py-0.5 text-[11px] font-medium text-warning"
              title="You have edits on the canvas that haven't been saved yet."
            >
              <span className="h-1.5 w-1.5 rounded-full bg-warning" />
              unsaved
            </span>
          )}

          {!wf.enabled && (
            <span
              className="inline-flex shrink-0 items-center gap-1 rounded-full bg-danger/15 px-2 py-0.5 text-[11px] font-medium text-danger"
              title="This workflow is disabled — triggers won't fire and webhooks return 404. Re-enable from the ⋮ menu."
            >
              <span className="h-1.5 w-1.5 rounded-full bg-danger" />
              disabled
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Step-debug pin pill — only shown when a "Run up to here"
              produced a persisted execution. Two affordances:
                * Continue   → fires a new test-run with
                               resume_from_execution_id = the pin
                * Clear (X)  → forgets the pin and wipes the canvas
                               overlay so the next plain Test run
                               starts visually clean. */}
          {pinnedExecutionId && pinnedNodeId && !isViewingPastRun && (
            <div className="inline-flex items-center gap-1 rounded-md border border-accent/40 bg-accent/10 px-2 py-1 text-xs">
              <span className="text-muted-foreground">Pinned at</span>
              <span className="font-mono font-medium text-accent">
                {nodes.find((n) => n.id === pinnedNodeId)?.data.label ?? pinnedNodeId}
              </span>
              <Button
                size="sm"
                variant="ghost"
                className="ml-1 h-6 gap-1 px-2"
                onClick={() => continueRunMutation.mutate()}
                disabled={continueRunMutation.isPending}
                title="Skip cached upstream nodes (handler bypassed, output reused from the pinned run) and execute only the downstream half of the DAG. Fast — typically only the nodes you've just edited actually run."
              >
                {continueRunMutation.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
                ) : (
                  <Play className="h-3 w-3" strokeWidth={2} />
                )}
                Continue
              </Button>
              <button
                type="button"
                onClick={() => {
                  setPinnedExecutionId(null);
                  setPinnedNodeId(null);
                  setTraceStatus({});
                }}
                aria-label="Clear pin"
                className="grid h-5 w-5 place-items-center rounded text-muted-foreground hover:bg-elevated hover:text-foreground"
              >
                ×
              </button>
            </div>
          )}
          {/* "Send test payload" works for ANY inbound workflow
              regardless of ingress type — webhook, sftp_pull, db_poll,
              etc. Backend resolves which sources are bound to the
              workflow and routes through the real intake path. We
              only surface it for direction=inbound (outbound workflows
              are triggered by canonical events, not external input). */}
          {wf.direction === "inbound" && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setWebhookOpen(true)}
              title="Send a payload through one of this workflow's bound ingress sources"
            >
              <Radio className="h-4 w-4" strokeWidth={1.5} />
              Send test payload
            </Button>
          )}
          {canWrite && (
            <>
              {/* Edit-state cluster: Undo · Redo · Discard. Grouped
                  visually so the user reads them as "fix what I just
                  did" actions, separate from the commit-state cluster
                  (Activate / Save) below. Discard is always shown but
                  disabled when there's nothing to discard — feels
                  more stable than appear/disappear on every edit. */}
              <div className="flex items-center gap-0.5 rounded-md border border-border bg-surface px-0.5 py-0.5">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={undo}
                  disabled={past.length === 0}
                  title="Undo (⌘Z)"
                  aria-label="Undo"
                  className="h-7 w-7 px-0"
                >
                  <Undo2 className="h-4 w-4" strokeWidth={1.5} />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={redo}
                  disabled={future.length === 0}
                  title="Redo (⇧⌘Z)"
                  aria-label="Redo"
                  className="h-7 w-7 px-0"
                >
                  <Redo2 className="h-4 w-4" strokeWidth={1.5} />
                </Button>
                <div className="mx-0.5 h-4 w-px bg-border" />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (
                      window.confirm(
                        "Discard ALL unsaved changes and reset the canvas to the loaded version's definition? This can't be undone.",
                      )
                    ) {
                      discardChanges();
                    }
                  }}
                  disabled={!isModified}
                  title={
                    isModified
                      ? "Discard changes — reset canvas to the loaded version"
                      : "Nothing to discard"
                  }
                  aria-label="Discard changes"
                  className="h-7 w-7 px-0"
                >
                  {/* Counter-clockwise rotate = "rewind / revert to
                      saved" — distinct from Trash (which the user
                      misread as "delete the workflow"). */}
                  <RotateCcw className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              </div>

              {/* "Activate this version" appears when browsing a non-
                  active version. One click rolls back (or promotes) —
                  no save needed because the version already exists. */}
              {!isViewingActive && viewedVersion && canActivate && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => activateMutation.mutate(viewedVersion.id)}
                  disabled={activateMutation.isPending}
                  title={`Make v${viewedVersion.version} the active version`}
                >
                  {activateMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4" strokeWidth={1.5} />
                  )}
                  Activate v{viewedVersion.version}
                </Button>
              )}

              {/* Save & activate is the primary commit action and stays
                  on the toolbar. "Save as new draft" was demoted to the
                  ⋮ menu — it's the rarer flow (you usually want the
                  changes live immediately in dev). */}
              <Button
                size="sm"
                onClick={() => saveMutation.mutate(true)}
                disabled={saveMutation.isPending || graphErrors.length > 0}
                title={
                  graphErrors.length > 0
                    ? `Fix ${graphErrors.length} graph error${graphErrors.length > 1 ? "s" : ""} before saving`
                    : "Save current canvas as a new version and activate it"
                }
              >
                {saveMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" strokeWidth={1.5} />
                )}
                Save &amp; activate
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" title="More actions">
                    <MoreVertical className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[14rem]">
                  {/* "Save as new draft" — promoted out of the always-on
                      toolbar (where it competed for visual weight with
                      Save & activate) into the menu. Rarer flow: usually
                      you want changes live immediately in dev. */}
                  <DropdownMenuItem
                    onClick={() => saveMutation.mutate(false)}
                    disabled={saveMutation.isPending || graphErrors.length > 0}
                  >
                    <Save className="h-4 w-4" strokeWidth={1.5} />
                    Save as new draft
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setEditMetadataOpen(true)}>
                    <Pencil className="h-4 w-4" strokeWidth={1.5} />
                    Edit metadata
                  </DropdownMenuItem>
                  {/* Duplicate forks the whole workflow into a new one
                      under "<name> (copy)" and navigates to the new
                      editor. Backend uses the active version as the
                      starting DAG; tags + description are carried
                      forward. Distinct from Cmd+D (duplicate the
                      selected node within the SAME workflow). */}
                  <DropdownMenuItem
                    onClick={() => duplicateMutation.mutate()}
                    disabled={duplicateMutation.isPending || !wf.active_version_id}
                  >
                    <Copy className="h-4 w-4" strokeWidth={1.5} />
                    Duplicate workflow
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => toggleEnabledMutation.mutate(!wf.enabled)}>
                    <Power className="h-4 w-4" strokeWidth={1.5} />
                    {wf.enabled ? "Disable" : "Enable"}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setDiffOpen(true)}
                    disabled={(wf.recent_versions?.length ?? 0) < 2}
                  >
                    <GitCompare className="h-4 w-4" strokeWidth={1.5} />
                    Compare versions
                  </DropdownMenuItem>
                  {canWrite && (
                    <DropdownMenuItem onClick={applyAutoLayout}>
                      <LayoutGrid className="h-4 w-4" strokeWidth={1.5} />
                      Auto-layout
                    </DropdownMenuItem>
                  )}

                  {/* Version switcher — moved here from the header so
                      the editor's top bar stays a single uncluttered
                      row. Power-user / rare-action; the status dot's
                      tooltip already surfaces which version is loaded. */}
                  {(wf.recent_versions?.length ?? 0) > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <div className="px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        Load version
                      </div>
                      {(wf.recent_versions ?? []).map((v) => {
                        const isActive = v.id === wf.active_version_id;
                        const isLoaded = v.id === effectiveVersionId;
                        return (
                          <DropdownMenuItem key={v.id} onClick={() => setLoadedVersionId(v.id)}>
                            <span className="font-mono text-xs">v{v.version}</span>
                            <span className="ml-auto flex items-center gap-1.5 text-[10px]">
                              {isActive && (
                                <Badge tone="active" className="text-[10px]">
                                  active
                                </Badge>
                              )}
                              {isLoaded && (
                                <Badge tone="neutral" className="text-[10px]">
                                  loaded
                                </Badge>
                              )}
                              {/* Discard a non-active version (draft-first
                                  cleanup). The API refuses a version with
                                  runs — that error surfaces in the save bar. */}
                              {canWrite && !isActive && (
                                <button
                                  type="button"
                                  title={`Delete v${v.version}`}
                                  className="rounded p-0.5 text-muted-foreground hover:text-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (
                                      window.confirm(
                                        `Delete version v${v.version}? This cannot be undone.\n\n` +
                                          `A version with past runs will be refused (it is history).`,
                                      )
                                    ) {
                                      deleteVersionMutation.mutate(v.id);
                                    }
                                  }}
                                >
                                  <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                                </button>
                              )}
                            </span>
                          </DropdownMenuItem>
                        );
                      })}
                    </>
                  )}

                  {canDelete && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          if (
                            window.confirm(
                              `Delete workflow "${wf.name}"? This cannot be undone.\n\n` +
                                `If there are any past executions, the API will refuse the delete; ` +
                                `disable it instead.`,
                            )
                          ) {
                            deleteMutation.mutate();
                          }
                        }}
                        className="text-danger"
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                        Delete workflow
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </div>

      {saveError && (
        <Alert tone="danger" className="mx-3 mt-2">
          {saveError}
        </Alert>
      )}

      {/* Editor body. Layout depends on view mode:
            - editor      → [canvas] [NodePalette on the RIGHT]
            - executions  → [Executions list on the LEFT] [canvas]
          The view-mode pill toggle is a floating overlay at the top
          center of the canvas — same place in both modes so it never
          shifts under the cursor. */}
      <div className="flex min-h-0 flex-1">
        {viewMode === "executions" && (
          <ExecutionsListSidebar
            workflowId={workflowId}
            selectedRunId={viewedRunId}
            onPickRun={setViewedRunId}
          />
        )}

        {/* Canvas + logs dock column. The dock is a sibling of the
            ReactFlow wrapper (not an absolute overlay) so the canvas
            actually shrinks when the user expands the logs panel. */}
        <div className="flex min-h-0 flex-1 flex-col">
          <div
            className="relative min-h-0 flex-1"
            ref={wrapperRef}
            onDragOver={onDragOver}
            onDrop={onDrop}
          >
            {/* Top-RIGHT floating "+" — opens the categorised
              ``PaletteDrawer`` (right-side sheet). Mirrors n8n's
              "What happens next?" panel: categories at level 1,
              nodes at level 2, search across all at the top.
              Hidden in Executions mode (read-only). */}
            {viewMode === "editor" && !isViewingPastRun && (
              <button
                type="button"
                title="Add node"
                onClick={() => setPaletteDrawerOpen(true)}
                className={cn(
                  "absolute right-3 top-2 z-10",
                  "flex h-8 w-8 items-center justify-center rounded-full",
                  "border border-border bg-surface/95 shadow-md backdrop-blur",
                  "text-foreground hover:bg-accent hover:text-accent-foreground",
                  "transition-transform hover:scale-110",
                )}
              >
                <PlusIcon className="h-4 w-4" strokeWidth={2.5} />
              </button>
            )}

            {/* Top-center pill toggle: Editor / Executions. The OUTER
              wrapper is ``pointer-events-none`` so clicks in the
              gutter around the pill pass through to the canvas (edge
              "+" / trash buttons that happen to live near the top
              would otherwise be eaten by this invisible bounding
              box). Only the inline pill itself catches pointers. */}
            <div className="pointer-events-none absolute left-1/2 top-2 z-10 -translate-x-1/2">
              <div className="pointer-events-auto inline-flex rounded-full border border-border bg-surface/95 p-0.5 shadow-md backdrop-blur">
                <button
                  type="button"
                  className={
                    "rounded-full px-4 py-1 text-xs font-medium transition " +
                    (viewMode === "editor"
                      ? "bg-foreground text-background"
                      : "text-muted-foreground hover:text-foreground")
                  }
                  onClick={() => setViewMode("editor")}
                >
                  Editor
                </button>
                <button
                  type="button"
                  className={
                    "rounded-full px-4 py-1 text-xs font-medium transition " +
                    (viewMode === "executions"
                      ? "bg-foreground text-background"
                      : "text-muted-foreground hover:text-foreground")
                  }
                  onClick={() => setViewMode("executions")}
                >
                  Executions
                </button>
              </div>
            </div>

            {/* Bottom-center floating Test Run.

              Mirrors the top Editor/Executions pill (same pill chrome,
              same horizontal centering, same z-10). Live on the canvas
              instead of the toolbar so it stays visually close to what
              you're about to run — and the toolbar can shed yet another
              button. Hidden in Executions mode and in past-run mode
              (both are read-only views; firing a new run from there
              would be confusing).

              Disabled when there are no nodes. In live-run mode
              (liveExecutionId set after a Test run click) it swaps to a
              spinner — matches the inline drawer behaviour. */}
            {viewMode === "editor" && !isViewingPastRun && (
              // bottom offset is RAISED on mobile so the pill clears the
              // browser's bottom toolbar (Safari/Chrome ~90px) + the iOS
              // home indicator (safe-area). Desktop keeps the flush bottom-4.
              <div className="pointer-events-auto absolute bottom-[calc(6rem_+_env(safe-area-inset-bottom))] left-1/2 z-10 -translate-x-1/2 sm:bottom-6">
                {liveExecutionId !== null ? (
                  // Live mode: pill becomes a clickable Stop. Cancels the
                  // backend execution (works for durable orch-webhooks
                  // runs via DBOS; sync test_run doesn't honour the
                  // signal but the UI still unblocks immediately) and
                  // wipes the canvas overlay so the user can iterate
                  // again without waiting.
                  <button
                    type="button"
                    onClick={async () => {
                      const eid = liveExecutionId;
                      // Optimistic: clear UI state first so the button
                      // disappears immediately even if cancel is slow.
                      setLiveExecutionId(null);
                      armedForLiveRef.current = false;
                      // Ignore the late sync-run result + wipe the overlay so
                      // no node stays animated after Stop.
                      runCancelledRef.current = true;
                      setTraceStatus({});
                      setNodeProgress({});
                      setNodeLiveCount({});
                      liveCountRef.current = newLiveCountState();
                      // Stop = reset the step-by-step cycle: drop the pinned
                      // execution so the "Pinned at … ▷ Continue" toolbar
                      // disappears (a stopped run isn't resumable).
                      setPinnedExecutionId(null);
                      setPinnedNodeId(null);
                      try {
                        await workflowExecutionsApi.cancel(eid);
                      } catch {
                        // Best effort — backend may already have finished.
                      }
                    }}
                    title="Stop the running execution"
                    className={
                      "inline-flex items-center gap-2 rounded-full border border-danger/60 " +
                      "bg-danger px-5 py-2 text-sm font-medium text-danger-foreground " +
                      "shadow-lg transition animate-pulse " +
                      "hover:scale-[1.02] hover:shadow-xl hover:bg-danger/90"
                    }
                  >
                    <Square className="h-4 w-4 fill-current" strokeWidth={2} />
                    Stop
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setTestRunOpen(true)}
                    disabled={nodes.length === 0}
                    title={
                      nodes.length === 0
                        ? "Add at least one node first"
                        : "Run with a sample payload (⌘↵)"
                    }
                    className={
                      "inline-flex items-center gap-2 rounded-full border border-border bg-foreground px-5 py-2 text-sm font-medium text-background shadow-lg transition " +
                      "hover:scale-[1.02] hover:shadow-xl " +
                      "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
                    }
                  >
                    <Play className="h-4 w-4 fill-current" strokeWidth={2} />
                    Test run
                  </button>
                )}
              </div>
            )}
            <ReactFlow
              nodes={nodesWithLeaf}
              edges={edgesForCanvas}
              // In view-past-run mode we still let ReactFlow process
              // selection changes (so the user can click a node and see
              // its recorded I/O) but block geometry / topology edits.
              nodesDraggable={!isViewingPastRun && canWrite}
              nodesConnectable={!isViewingPastRun && canWrite}
              elementsSelectable
              edgesUpdatable={!isViewingPastRun}
              onNodesChange={(changes: NodeChange[]) => {
                if (isViewingPastRun) {
                  // Allow only selection / dimension-change events
                  // through — discard everything that mutates the graph.
                  const safe = changes.filter(
                    (c) => c.type === "select" || c.type === "dimensions",
                  );
                  onNodesChange(safe);
                } else {
                  onNodesChange(changes);
                }
              }}
              onEdgesChange={(changes) => {
                if (isViewingPastRun) return;
                // Edge removal goes through here too (e.g. select edge +
                // Delete key). Snapshot BEFORE the change so undo can
                // restore the edge.
                if (changes.some((c) => c.type === "remove")) pushHistory();
                onEdgesChange(changes);
              }}
              // Snapshot at drag START — one undo entry per drag gesture,
              // not one per pixel of movement. ReactFlow pumps `position`
              // changes through onNodesChange at ~60 Hz during drag; if
              // we snapshotted on each we'd fill the stack instantly.
              onNodeDragStart={() => {
                if (!isViewingPastRun) pushHistory();
              }}
              // Persist a sub-flow region's new canvas position onto the
              // parent sink branch so it survives save/reload (the region
              // wrapper itself isn't part of the main definition). Other
              // node drags are already captured by flowToTray at save time.
              onNodeDragStop={(_, node) => {
                if (isViewingPastRun) return;
                if (node.type !== "subflowRegion") return;
                const sfId = workflowIdFromGroupId(node.id);
                if (sfId) persistRegionPosition(sfId, node.position);
              }}
              onConnect={onConnect}
              isValidConnection={isValidConnection}
              // Forgiving drop radius so a finger doesn't have to land dead-on
              // the (small) target handle — matters most on touch/mobile.
              connectionRadius={34}
              onSelectionChange={(sel) => {
                const first = sel.nodes[0];
                setSelectedId(first ? first.id : null);
                // Debug affordance: while a run's trace is loaded, clicking a
                // node surfaces its input/output for THIS execution — make sure
                // the bottom run-details dock is open so the detail is visible
                // (it focuses the selected node via ``selectedNodeId``).
                if (first && activeRunDetail) setLogsOpen(true);
              }}
              // Double-click is the explicit "open inspector" gesture.
              // Single-click only selects the node (highlight + hotkeys).
              onNodeDoubleClick={(_, n) => setDrawerNodeId(n.id)}
              // Right-click a node → same context menu as the ⋮ toolbar button.
              onNodeContextMenu={(e, n) => {
                if (isViewingPastRun) return;
                e.preventDefault();
                setNodeMenu({ id: n.id, x: e.clientX, y: e.clientY });
              }}
              nodeTypes={NODE_TYPES}
              edgeTypes={EDGE_TYPES}
              fitView
              proOptions={{ hideAttribution: true }}
              // Don't auto-select a node when the user starts dragging
              // it — that would fire onSelectionChange → open the editor
              // drawer mid-drag, which is jarring. Selection happens only
              // on an explicit click.
              selectNodesOnDrag={false}
            >
              <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
              {/* Lift the ReactFlow chrome off the very bottom edge so it
                doesn't feel glued to the canvas floor (default is ~15px). */}
              <Controls position="bottom-left" style={{ bottom: 22 }} />
              <MiniMap pannable zoomable className="!bg-surface" style={{ bottom: 22 }} />
            </ReactFlow>

            {/* N8N-style "insert node" popover. Two trigger paths share
              the same component:
                * edgeInsert  — "+" clicked on an existing edge → the
                  new node is spliced INTO that edge.
                * leafAdd     — "+" clicked on a leaf node → the new
                  node is APPENDED after it via a single edge.
              ``edgeInsert`` wins when both are set (shouldn't happen
              in practice; opening one closes the other). */}
            {edgeInsert && (
              <PalettePopover
                position={edgeInsert.screenPos}
                catalog={Object.values(catalogIdx)}
                workflowKind={wf?.kind ?? "orchestration"}
                direction={wf?.kind === "canonical" ? direction : undefined}
                onPick={(kind) => insertNodeOnEdge(edgeInsert.edgeId, kind)}
                onCancel={() => setEdgeInsert(null)}
              />
            )}
            {leafAdd && !edgeInsert && (
              <PalettePopover
                position={leafAdd.screenPos}
                catalog={Object.values(catalogIdx)}
                workflowKind={wf?.kind ?? "orchestration"}
                direction={wf?.kind === "canonical" ? direction : undefined}
                onPick={(kind) => {
                  addNodeAfter(leafAdd.sourceId, kind);
                  setLeafAdd(null);
                }}
                onCancel={() => setLeafAdd(null)}
              />
            )}
            {/* Live sub-workflow drill-in, opened by the "⤢" corner
              button on a call_workflow node. Read-only canvas that
              overlays the child's SSE while the parent run is live
              (child events ride ``liveExecutionId``). */}
            {watchSubflowId && (
              <SubWorkflowRunDialog
                childWorkflowId={watchSubflowId}
                executionId={liveExecutionId}
                onClose={() => setWatchSubflowId(null)}
              />
            )}
            {/* Categorised right-side drawer for the top-right "+"
              button. Distinct from the contextual edge / leaf
              popovers — those stay small + anchored at the cursor
              for quick mid-flow insertions, the drawer is for
              browsing. */}
            <PaletteDrawer
              open={paletteDrawerOpen}
              onOpenChange={setPaletteDrawerOpen}
              catalog={Object.values(catalogIdx)}
              workflowKind={wf?.kind ?? "orchestration"}
              direction={wf?.kind === "canonical" ? direction : undefined}
              onPick={addNodeStandalone}
            />

            {/* Floating validation panel — top-right of the canvas, only
              shows when there are issues. Click a row to (eventually)
              focus the offending node; for now it just lists them. */}
            {graphIssues.length > 0 &&
              (issuesPanelOpen || graphErrors.length > 0 ? (
                // Expanded panel. Anchored top-LEFT (the right edge is where
                // the graph flows into the last node's "+" add affordance, so a
                // right-anchored panel covered it); a header row lets the user
                // collapse it back to the chip.
                <div className="pointer-events-auto absolute left-3 top-3 z-10 max-w-sm space-y-1 rounded-md border border-border bg-surface/95 p-2 text-xs shadow-md backdrop-blur">
                  <div className="flex items-center justify-between gap-2 px-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <span>Graph issues</span>
                    <div className="flex items-center gap-2">
                      <span>
                        {graphErrors.length > 0 && (
                          <span className="text-danger">
                            {graphErrors.length} error{graphErrors.length > 1 ? "s" : ""}
                          </span>
                        )}
                        {graphErrors.length > 0 && graphWarnings.length > 0 && " · "}
                        {graphWarnings.length > 0 && (
                          <span className="text-amber-600">
                            {graphWarnings.length} warning{graphWarnings.length > 1 ? "s" : ""}
                          </span>
                        )}
                      </span>
                      {/* Collapse — hidden when there are errors (they must stay
                        visible since they block Save). */}
                      {graphErrors.length === 0 && (
                        <button
                          type="button"
                          onClick={() => setIssuesPanelOpen(false)}
                          title="Collapse"
                          aria-label="Collapse graph issues"
                          className="rounded p-0.5 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                        >
                          <X className="h-3 w-3" strokeWidth={1.5} />
                        </button>
                      )}
                    </div>
                  </div>
                  <ul className="max-h-48 space-y-0.5 overflow-y-auto">
                    {graphIssues.map((issue, i) => (
                      <li
                        key={i}
                        className={
                          "cursor-pointer rounded px-2 py-1 hover:bg-muted/60 " +
                          (issue.severity === "error" ? "text-danger" : "text-amber-700")
                        }
                        onClick={() => {
                          // Focus + open the drawer on the first offending
                          // node — clicking an error/warning row should jump
                          // straight to the place that needs fixing.
                          const targetId = issue.nodeIds[0];
                          if (!targetId) return;
                          setNodes((nds) =>
                            nds.map((n) => ({ ...n, selected: n.id === targetId })),
                          );
                          setSelectedId(targetId);
                          setDrawerNodeId(targetId);
                        }}
                        title="Click to focus the offending node and open its inspector"
                      >
                        <span className="font-mono text-[10px] uppercase">
                          {issue.severity === "error" ? "ERR" : "WARN"}
                        </span>{" "}
                        {issue.message}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                // Collapsed chip — anchored top-LEFT so it never overlaps the
                // rightmost node's "+" add-node button. Click to expand.
                <button
                  type="button"
                  onClick={() => setIssuesPanelOpen(true)}
                  title="Show graph issues"
                  className="pointer-events-auto absolute left-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-surface/95 px-2.5 py-1 text-[11px] font-medium text-amber-700 shadow-sm hover:bg-muted/60"
                >
                  <AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {graphWarnings.length} warning{graphWarnings.length > 1 ? "s" : ""}
                </button>
              ))}
          </div>

          {/* Run details — n8n-style bottom dock. SIBLING of the
              ReactFlow wrapper, not absolute over it, so the canvas
              shrinks when the dock opens (no more logs hiding the
              bottom of the graph). Height is owned + persisted by the
              editor and clamped against viewport in RunDetailsPanel
              itself. */}
          <RunDetailsPanel
            run={activeRunDetail}
            selectedNodeId={selectedId}
            onSelectNode={(nodeId) => {
              setNodes((nds) => nds.map((n) => ({ ...n, selected: n.id === nodeId })));
              setSelectedId(nodeId);
            }}
            open={logsOpen}
            onOpenChange={setLogsOpen}
            height={logsHeight}
            onHeightChange={setLogsHeight}
            liveExecutionId={liveExecutionId}
          />
        </div>

        {/* NodePalette sidebar removed in favour of the N8N-style
            on-demand palette: click "+" on an edge (intermediate
            insertion) or click "+" at the end of a leaf node
            (extension). For the empty-canvas case the canvas itself
            shows a centered "Add first node" overlay (see below).
            The sidebar still ships as a component for any other
            caller; only the editor's mounting was dropped. */}

        {/* NodeEditorDrawer is rendered at the page root (below) so it
            overlays the entire editor including the palette — n8n
            style. When closed it's an empty fragment. The right-side
            inspector is intentionally GONE from the editor body: the
            drawer replaces it. */}
      </div>

      <SendTestPayloadDialog
        open={webhookOpen}
        onOpenChange={setWebhookOpen}
        workflowId={workflowId}
      />

      <VersionDiffDialog
        workflowId={workflowId}
        versions={wf.recent_versions ?? []}
        open={diffOpen}
        onOpenChange={setDiffOpen}
      />

      <WorkflowEditMetadataDialog
        workflow={wf}
        open={editMetadataOpen}
        onOpenChange={setEditMetadataOpen}
        errorWorkflowId={errorWorkflowId}
        onErrorWorkflowIdChange={setErrorWorkflowId}
      />

      {/* Node editor — opens on double-click of a node (single click
          only selects). Replaces the old right-side inspector with a
          3-column drawer. The selection-overlay loop fix from before
          (clearing ReactFlow's internal ``selected`` flag) is no longer
          needed because closing the drawer doesn't touch selection at
          all — they're independent now. */}
      <NodeEditorDrawer
        node={drawerTrayNode}
        spec={drawerSpec ?? null}
        // Bumped by the canvas "Run node" affordance → the drawer auto-fires
        // Execute Node once for this open (prompting for input when needed).
        autoExecuteToken={autoRunToken}
        // ADR-0011 — full catalog so the drawer can offer the Action
        // switcher for integration nodes (Postgres select↔insert↔…).
        catalog={Object.values(catalogIdx)}
        workflowId={workflowId}
        // ADR-0010 D10 — for app-kind workflows, hand the bound app's id
        // down so the row_changed/data_table SmartPickers can list its
        // data tables. Undefined for canonical/orchestration.
        appId={wfQuery.data?.app_id ?? undefined}
        // Hand the SmartPicker the canvas-local nodes so node_id
        // pickers (Branch true/false target, future Switch cases) can
        // dropdown the currently-existing siblings.
        nodesInCanvas={nodes.map((n) => ({
          id: n.id,
          label: n.data.label,
          kind: n.data.kind,
        }))}
        // ADR-0012 — edges + per-node outputs power the INPUT pane's
        // upstream "N nodes back" picker.
        edges={edges.map((e) => ({ source: e.source, target: e.target }))}
        nodeOutputs={drawerNodeOutputs}
        nodeLogOutputs={drawerNodeLogOutputs}
        // Full per-node traces from the live test-run — the drawer seeds its
        // INPUT/OUTPUT panes from this on open so re-opening a node after a
        // test-run keeps showing what flowed through it.
        nodeTraces={drawerNodeTraces}
        // Live I/O: while a test-run is going, the drawer fetches the open
        // node's just-persisted trace on its node_finished SSE (fetch-on-
        // demand over the progressive per-node persistence).
        liveExecutionId={liveExecutionId}
        // Outgoing edge handles of the drawer-open node — SwitchCasesEditor
        // uses this to surface "orphan edge" warnings when a case is
        // removed but the edge attached to its handle is still there.
        outgoingEdgeHandles={
          drawerNodeId
            ? edges
                .filter((e) => e.source === drawerNodeId && e.sourceHandle)
                .map((e) => e.sourceHandle as string)
            : []
        }
        onClose={() => setDrawerNodeId(null)}
        onChange={patchNode}
        onDelete={deleteNode}
        onDuplicate={(id) => duplicateNode(id)}
        buildDefinition={buildLiveDefinition}
        onExecuteComplete={(result) => {
          // Same wiring as the TestRunDrawer's onRunComplete: light up
          // the canvas with per-node statuses + populate the bottom
          // logs panel + auto-open it. Without this, closing the node
          // modal after Execute would lose all visual trace of the
          // run that just happened.
          const map: Record<string, string> = {};
          result.node_traces.forEach((t) => {
            map[t.node_id] = t.status;
          });
          // MERGE, don't replace: a loop body runs in a nested executor whose
          // per-iteration nodes may NOT be in the top-level node_traces. They
          // WERE lit up by the live SSE overlay (prev), so overwriting with a
          // map missing them would wipe their green ring — making executed
          // loop-body nodes look like they never ran. prev keeps them; map
          // overlays the authoritative top-level final statuses.
          setTraceStatus((prev) => ({ ...prev, ...map }));
          // Sync completion → exit live mode so the traversed edges stop
          // animating (marching-ants over a panned canvas = the whole-app
          // paint flicker). Same reasoning as onRunComplete.
          armedForLiveRef.current = false;
          setLiveExecutionId(null);
          setEditorTestRun(result);
          setLogsOpen(true);
          // Step-by-step: PIN this run so the NEXT Execute Node (on the same
          // structural graph) RESUMES from it instead of restarting from the
          // trigger (no re-prompt for input). Only on success — a failed run
          // isn't a useful base to continue from.
          if (result.status === "succeeded" && result.execution_id && drawerNodeId) {
            setPinnedExecutionId(String(result.execution_id));
            setPinnedNodeId(drawerNodeId);
            pinnedSignatureRef.current = structuralSignature;
            setPinNonce((n) => n + 1);
          }
        }}
        // TE-F — position-aware step-by-step: same pinned node → re-execute;
        // a node downstream of the pin → continue (resume); upstream / unrelated
        // → fresh run (null).
        resumeFromExecutionId={resumeInfo.executionId}
        resumeKind={resumeInfo.kind}
        // When we're viewing a past run, hand the drawer the recorded
        // per-node execution(s) for the drawer's node so it can show
        // the actual input/output that flowed through — instead of (or
        // in addition to) the live editable config.
        viewedRunNodeExecutions={
          isViewingPastRun && drawerNodeId
            ? viewedRunQ.data?.nodes.filter((n) => n.node_id === drawerNodeId)
            : undefined
        }
        readOnly={isViewingPastRun || !canWrite}
      />

      {/* N8N-style node context menu (⋮ toolbar button OR right-click). */}
      {nodeMenu && (
        <NodeContextMenu
          x={nodeMenu.x}
          y={nodeMenu.y}
          canWrite={canWrite}
          onClose={() => setNodeMenu(null)}
          onOpen={() => {
            setDrawerNodeId(nodeMenu.id);
            setNodeMenu(null);
          }}
          onRun={() => {
            runNodeFromCanvas(nodeMenu.id);
            setNodeMenu(null);
          }}
          onDuplicate={() => {
            duplicateNode(nodeMenu.id);
            setNodeMenu(null);
          }}
          onDelete={() => {
            const n = nodes.find((x) => x.id === nodeMenu.id);
            const name = n?.data.label || n?.data.spec?.label || n?.data.kind || nodeMenu.id;
            if (window.confirm(`Eliminare il nodo "${name}"?`)) {
              deleteNode(nodeMenu.id);
            }
            setNodeMenu(null);
          }}
        />
      )}

      <TestRunDrawer
        open={testRunOpen}
        onOpenChange={setTestRunOpen}
        workflowId={workflowId}
        buildDefinition={buildLiveDefinition}
        onTraceUpdate={(trace) => {
          if (runCancelledRef.current) return; // stopped → ignore late trace
          const map: Record<string, string> = {};
          trace.forEach((t) => {
            map[t.node_id] = t.status;
          });
          // Merge (see onExecuteComplete): preserve live-lit nested loop-body
          // nodes that the top-level trace may omit.
          setTraceStatus((prev) => ({ ...prev, ...map }));
        }}
        onRunStart={() => {
          // Fresh run → clear the "stopped" guard from any previous Stop.
          runCancelledRef.current = false;
          // Auto-close the input drawer: once the run is fired the user
          // wants the canvas, not the payload form.
          setTestRunOpen(false);
          // Arm the SSE listener so the next EXECUTION_STARTED event
          // for this workflow becomes our liveExecutionId — the canvas
          // can then light up nodes in real time.
          armedForLiveRef.current = true;
          // Open the bottom logs panel IMMEDIATELY so the user sees
          // node traces stream in live (instead of only after the run
          // completes). Wipe any stale test-run result so the panel
          // starts empty and fills as events arrive.
          setEditorTestRun(null);
          setLogsOpen(true);
        }}
        onRunComplete={(result) => {
          // The user hit Stop while this (uncancellable) sync run was still
          // in flight — its late result must NOT re-light the canvas.
          if (runCancelledRef.current) return;
          // The synchronous test-run response IS the authoritative "run
          // finished" signal — exit live mode NOW instead of waiting for the
          // SSE EXECUTION_FINISHED (which can race/be missed). Leaving
          // ``liveExecutionId`` set keeps the traversed edges ``animated``
          // (marching-ants); panning the canvas then composites an animating
          // SVG over a moving transform → a full-window paint flicker. Also
          // disarm so a late-buffered EXECUTION_STARTED can't re-arm live mode
          // after the run is already done.
          armedForLiveRef.current = false;
          setLiveExecutionId(null);
          // Populate the bottom RunDetailsPanel + auto-open so the
          // user sees the trace without keeping this drawer open.
          setEditorTestRun(result);
          setLogsOpen(true);
          // Step-debug bookkeeping: if this run was armed with a stop
          // point, the returned execution becomes the new pin (or
          // replaces the previous one). On a plain run (no stop) we
          // CLEAR the pin so the canvas badge doesn't lie about a
          // stale halt point.
          if (pendingStopAt && result.execution_id) {
            setPinnedExecutionId(String(result.execution_id));
            setPinnedNodeId(pendingStopAt);
          } else {
            // Plain test-run (no stop point) → clear any stale pin so
            // the toolbar badge can't lie about a stale halt point.
            setPinnedExecutionId(null);
            setPinnedNodeId(null);
          }
          setPendingStopAt(null);
        }}
        stopAfterNodeId={pendingStopAt}
        stopAfterNodeLabel={
          pendingStopAt
            ? (nodes.find((n) => n.id === pendingStopAt)?.data.label ?? pendingStopAt)
            : null
        }
        /* resume is fired directly via continueRunMutation — the
           drawer is no longer involved in the resume flow. */
        resumeFromExecutionId={null}
      />
    </div>
  );
}

export function WorkflowEditorPage() {
  const { workflowId } = useParams<{ workflowId: string }>();
  if (!workflowId) return null;
  return (
    <ReactFlowProvider>
      <EditorInner workflowId={workflowId} />
    </ReactFlowProvider>
  );
}
